// PublicarProductoML — /admin/canales/mercadolibre/publicar/:productoId
// Wizard de 10 pasos para armar una publicación de ML.
// Mientras LIMPACKUY siga sin OAuth, el último paso guarda como draft (status=draft).
// Cuando esté conectado, llamamos POST /items y queda active.

import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

// Trae los atributos de una categoría de ML pasando por la edge fn cache `ml-categoria-attrs`
// (tabla ml_categoria_attrs_cache, revalida cada 24h) → evita pegarle a ML en cada publicación.
// Si la edge fn falla por lo que sea, cae al endpoint público de ML directo (mismo shape).
export async function fetchCategoriaAttrs(categoriaId, signal) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-categoria-attrs`, {
      method: 'POST', signal,
      headers: {
        'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ categoria_id: categoriaId }),
    })
    if (r.ok) {
      const d = await r.json()
      if (Array.isArray(d?.attrs)) return d.attrs
    }
  } catch (_) { /* cae al fetch directo abajo */ }
  const r2 = await fetch(`https://api.mercadolibre.com/categories/${categoriaId}/attributes`, { signal })
  if (!r2.ok) throw new Error(`ML ${r2.status}`)
  const d2 = await r2.json()
  return Array.isArray(d2) ? d2 : []
}

// Árbol de categorías de ML Uruguay bajo "Cuidado del Hogar y Lavandería" (MLU436069).
// Priorizamos categorías HOJA (más específicas) porque ML rankea mejor lo publicado en hoja.
// Actualizado 2026-07-10 con los nombres OFICIALES de la API ML + subcategorías relevantes para Limpack.
const CATEGORIAS_LIMPIEZA = [
  // Hoja de "Productos de Limpieza" (MLU442578) — más específicas
  { id: 'MLU442584', label: 'Lavandinas',                       grupo: 'Productos de Limpieza' },
  { id: 'MLU442586', label: 'Limpiadores Multiuso',             grupo: 'Productos de Limpieza' },
  { id: 'MLU189737', label: 'Detergentes',                      grupo: 'Productos de Limpieza' },
  { id: 'MLU442587', label: 'Desengrasantes',                   grupo: 'Productos de Limpieza' },
  { id: 'MLU189736', label: 'Aromatizadores',                   grupo: 'Productos de Limpieza' },
  { id: 'MLU417852', label: 'Toallitas Desinfectantes',         grupo: 'Productos de Limpieza' },
  { id: 'MLU388722', label: 'Ceras para Piso',                  grupo: 'Productos de Limpieza' },
  { id: 'MLU388723', label: 'Desodorantes para Piso',           grupo: 'Productos de Limpieza' },
  { id: 'MLU417808', label: 'Desodorantes para Inodoros',       grupo: 'Productos de Limpieza' },
  { id: 'MLU416654', label: 'Destapa Cañerías Químicos',        grupo: 'Productos de Limpieza' },
  { id: 'MLU417311', label: 'Limpiadores de Alfombras',         grupo: 'Productos de Limpieza' },

  // Hojas de "Desechables" (MLU436104)
  { id: 'MLU189738', label: 'Papeles Higiénicos',               grupo: 'Desechables' },
  { id: 'MLU388906', label: 'Rollos de Cocina',                 grupo: 'Desechables' },
  { id: 'MLU413219', label: 'Servilletas de Papel',             grupo: 'Desechables' },
  { id: 'MLU388855', label: 'Bolsas de Basura',                 grupo: 'Desechables' },
  { id: 'MLU416824', label: 'Bolsas para Freezer',              grupo: 'Desechables' },
  { id: 'MLU413160', label: 'Papel Aluminio',                   grupo: 'Desechables' },
  { id: 'MLU413156', label: 'Papel Film',                       grupo: 'Desechables' },

  // Hojas de "Productos de Lavandería" (MLU407832 padre — NO usar, listing_allowed:false)
  // Agregadas 15-jul-2026: el predictor de ML sugería categorías equivocadas para "lavarropas"
  // porque no las teníamos en la lista interna. BR13/BR14/BR15/BR16/BR17 ya vivos en estas hojas.
  // FIX 04-ago-2026: MLU436133 (Quitamanchas) NO existe (404) y MLU436135 era "Sets de Duchador y
  // Grifería" (¡Construcción!) — verificado contra la API. Corregidos a las hojas reales.
  { id: 'MLU436132', label: 'Jabón para Ropa (líquido)',        grupo: 'Lavandería' },
  { id: 'MLU416538', label: 'Panes de Jabón (barra)',           grupo: 'Lavandería' },
  { id: 'MLU407838', label: 'Suavizantes para Ropa',            grupo: 'Lavandería' },
  { id: 'MLU407836', label: 'Quitamanchas para Ropa',           grupo: 'Lavandería' },
  { id: 'MLU407835', label: 'Perfumes para Ropa',               grupo: 'Lavandería' },

  // Accesorios de limpieza — hojas listables verificadas contra la API 04-ago-2026.
  // Antes: MLU189726 y MLU436100, que son PADRES no listables (ML rechazaba el POST).
  { id: 'MLU442589', label: 'Esponjas de Limpieza',             grupo: 'Accesorios' },
  { id: 'MLU416857', label: 'Guantes de Limpieza',              grupo: 'Accesorios' },
  { id: 'MLU388817', label: 'Trapos y Paños de Limpieza',       grupo: 'Accesorios' },
  { id: 'MLU442591', label: 'Cepillos de Limpieza',             grupo: 'Accesorios' },
  { id: 'MLU189733', label: 'Escobas y Escobillones',           grupo: 'Accesorios' },
  // Accesorios de lavandería — hojas listables verificadas (perchas cuelga de otra rama).
  { id: 'MLU414001', label: 'Broches para Ropa',                grupo: 'Lavandería acc.' },
  { id: 'MLU436112', label: 'Cestos de Ropa',                   grupo: 'Lavandería acc.' },
  { id: 'MLU388187', label: 'Perchas',                          grupo: 'Lavandería acc.' },

  // Padres genéricos (fallback)
  { id: 'MLU442578', label: 'Productos de Limpieza (genérico)', grupo: 'Genéricos' },
  { id: 'MLU436104', label: 'Desechables (genérico)',           grupo: 'Genéricos' },
]
// Los 3 listing types REALES de MLU, verificados el 15-jul-2026 contra
// GET /users/{id}/available_listing_types y GET /sites/MLU/listing_prices.
// ⚠ NO agregar `gold_pro`: NO EXISTE en MLU (es de MLA/Argentina). Mandarlo hace que
// ML devuelva un 500 "Not in GZIP format" imposible de diagnosticar. Ver BR16 / ml_logs.
// Los nombres (Clásica / Gratuita / Premium) son los que devuelve ML — respetarlos:
// la versión vieja de este archivo llamaba "Clásica" a gold_special, que es Premium.
// Orden alfabético por id. La elección es siempre manual: sin default ni recomendación.
const LISTING_TYPES = [
  {
    id: 'bronze',
    label: 'Clásica',
    comision: '0% + costo fijo por venta',
    exposicion: 'Alta',
    duracion: 'Ilimitada',
    cuotas: 'Sin cuotas sin interés',
    fee: { pct: 0 },
  },
  {
    id: 'free',
    label: 'Gratuita',
    comision: 'Sin costo',
    exposicion: 'Mínima',
    duracion: '60 días',
    cuotas: 'Sin cuotas sin interés',
    nota: 'Cupo limitado de publicaciones activas gratuitas en toda la cuenta; se libera al pausar o vender.',
    fee: { pct: 0, sinFijo: true },
  },
  {
    id: 'gold_special',
    label: 'Premium',
    comision: '13,5% + costo fijo por venta',
    exposicion: 'Máxima',
    duracion: 'Ilimitada',
    cuotas: 'Cuotas sin interés al comprador',
    fee: { pct: 13.5 },
  },
]
const SHIPPING_MODES = [
  { id: 'me2',    label: 'Mercado Envíos', meta: 'Según tu cuenta: Flex (repartís vos, mismo día en Montevideo) o dejando en sucursal DAC/Correo · cobertura nacional' },
  { id: 'custom', label: 'Coordinar con vendedor', meta: 'Sin Mercado Envíos · pierde garantía ML · NO recomendado' },
]

// Costo fijo por venta según tramo de precio. Los rangos de app_config.ml_costo_fijo_rangos
// (Roberto, 30-jun) se verificaron el 15-jul-2026 contra GET /sites/MLU/listing_prices y coinciden
// exactos con ML: <$500 → $15 · <$750 → $25 · <$1000 → $40 · desde $1000 → sin fijo.
const RANGOS_FIJO_FALLBACK = [{ hasta: 500, fijo: 15 }, { hasta: 750, fijo: 25 }, { hasta: 1000, fijo: 40 }]
function costoFijoML(precio, rangos) {
  const p = Number(precio) || 0
  const r = (rangos?.length ? rangos : RANGOS_FIJO_FALLBACK).slice().sort((a, b) => a.hasta - b.hasta)
  return r.find(x => p < x.hasta)?.fijo ?? 0
}

// Comisión oficial de ML por tipo de publicación (MLU, verificado 15-jul-2026 contra
// GET /sites/MLU/listing_prices). Reemplaza al viejo `ml_comision_pct` fijo, que aplicaba un mismo
// porcentaje a todos los tipos y mentía en el neto del paso 7: en bronze/free NO hay comisión
// porcentual. Devuelve el desglose para poder mostrarlo, no solo el total.
// Fórmula ML: precio × pct + fijo_del_tramo. Ejemplos verificados contra la API:
//   gold_special $600 → 600×0,135 + 25 = 106   ·   bronze $600 → 25   ·   free → 0
export function calcularComisionML(precio, listingType, rangos) {
  const p = Number(precio) || 0
  const t = LISTING_TYPES.find(x => x.id === listingType)
  if (!t) return { pct: 0, variable: 0, fijo: 0, total: 0, conocido: false }
  const variable = p * (t.fee.pct / 100)
  const fijo = t.fee.sinFijo ? 0 : costoFijoML(p, rangos)
  return { pct: t.fee.pct, variable: Math.round(variable), fijo, total: Math.round(variable + fijo), conocido: true }
}

// Sugerencia de family_name. OJO: en el pipeline User Products de ML, family_name ES el título
// público (ML lo copia tal cual a `title`), así que esto es solo un punto de partida editable —
// el heurístico se come palabras clave (ej. "Lavandina"). La verdad es lo que Nico deja en el paso 4.
// Misma lógica que la edge fn ml-publicar-producto v1.11: si cambia una, cambiar la otra.
export function derivarFamilyName(titulo, marca) {
  let t = (titulo || '').trim()
  const size = (t.match(/\d+(?:[.,]\d+)?\s*(?:l|ml|lt|cc|kg|g|gr)\b/i) || [''])[0].trim()
  t = t.replace(/\d+(?:[.,]\d+)?\s*(?:l|ml|lt|cc|kg|g|gr)\b/ig, ' ')
  if (marca) {
    for (const tok of marca.split(/\s+/).filter(Boolean)) {
      t = t.replace(new RegExp(`\\b${tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'ig'), ' ')
    }
  }
  const palabras = t.split(/\s+/).filter(Boolean)
  if (palabras.length > 2) palabras.pop()
  const base = palabras.join(' ').trim()
  return [base, marca, size].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim().slice(0, 60)
}
const STEPS = [
  { id: 1,  label: 'Producto',          sub: 'Del catálogo Limpack' },
  { id: 2,  label: 'Categoría ML',      sub: 'Limpieza Hogar UY' },
  { id: 3,  label: 'Atributos',         sub: 'BRAND + PRODUCT_NAME' },
  { id: 4,  label: 'Título',            sub: 'Max 60 caracteres' },
  { id: 5,  label: 'Descripción',       sub: 'Markdown soportado' },
  { id: 6,  label: 'Fotos',             sub: 'Hasta 12 · primera es portada' },
  { id: 7,  label: 'Precio y stock',    sub: 'Markup editable' },
  { id: 8,  label: 'Tipo de listing',   sub: 'Se elige por producto' },
  { id: 9,  label: 'Envío',             sub: 'Mercado Envíos · dejás en sucursal' },
  { id: 10, label: 'Preview y publicar', sub: 'Guardar draft o salir vivo' },
]

export default function PublicarProductoML() {
  const { productoId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [producto, setProducto] = useState(null)
  const [config, setConfig] = useState({ markup_default: 28, comision_pct: 13.5, costo_fijo_rangos: [{ hasta: 500, fijo: 15 }, { hasta: 750, fijo: 25 }, { hasta: 1000, fijo: 40 }], descripcion_bloques: [] })
  const [step, setStep] = useState(1)
  const [pub, setPub] = useState({
    categoria_id: 'MLU442578',
    titulo: '',
    // family_name = título público real en el pipeline User Products. Se edita en el paso 4.
    family_name: '',
    descripcion: '',
    precio: 0,
    // Sin default a propósito: el tipo de publicación se elige por producto en el paso 8.
    listing_type: null,
    available_quantity: 1,
    condition: 'new',
    shipping_mode: 'me2',
    free_shipping: false,
    attributes: { BRAND: '', PRODUCT_NAME: '' },
  })
  const [fotos, setFotos] = useState([])
  const [msg, setMsg] = useState(null)
  const [saving, setSaving] = useState(false)
  const [previewPayload, setPreviewPayload] = useState(null)  // resultado del dry-run
  const [tieneConexion, setTieneConexion] = useState(false)
  const [publicacionId, setPublicacionId] = useState(null)
  // Publicación activa de la misma marca+categoría: ML la trata como la misma familia de variantes.
  // Si existe, el paso 4 avisa antes de heredar su family_name — nada silencioso.
  const [familiaHermano, setFamiliaHermano] = useState(null)
  const [autoSaved, setAutoSaved] = useState(null)
  const [fotoGrande, setFotoGrande] = useState(null)
  // Publicación YA activa/pausada de este mismo producto → evita crear un draft duplicado y
  // publicar dos veces el mismo ítem (ML lo modera). Se detecta al cargar.
  const [yaPublicado, setYaPublicado] = useState(null)

  // ── Posición y portada de las fotos ML ──────────────────────────────────────
  // BUG (27-jul): la posición se calculaba como `fotos.length`. Si se borra una foto del
  // medio quedan posiciones [0,1,3] → length=3 → la nueva entra en 3 y PISA a la existente.
  // Pasó con EC05 (0,1,3,3) y BR17 (0,2,2): posiciones duplicadas + huecos, y al publicar
  // en ML el orden de las fotos salía cualquiera.
  const proximaPosicion = () =>
    fotos.length === 0 ? 0 : Math.max(...fotos.map(f => Number(f.posicion) || 0)) + 1
  // Portada: debe serlo si NO hay ninguna marcada. Antes se asumía "la primera que entra";
  // si esa se borraba, el producto quedaba sin portada (caso CA07).
  const faltaPortada = () => !fotos.some(f => f.es_portada)

  async function cargar() {
    setLoading(true)
    const [prodRes, confRes, fotosRes, credRes, draftRes, activaRes] = await Promise.all([
      supabase.from('productos').select('*').eq('id', productoId).maybeSingle(),
      supabase.from('app_config').select('key, value').like('key', 'ml_%'),
      supabase.from('ml_fotos').select('*').eq('producto_id', productoId).order('posicion'),
      supabase.from('ml_credentials').select('access_token').maybeSingle(),
      supabase.from('ml_publicaciones').select('*').eq('producto_id', productoId).eq('status', 'draft').order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('ml_publicaciones').select('id, ml_item_id, permalink, status').eq('producto_id', productoId).in('status', ['active', 'paused']).order('published_at', { ascending: false }).limit(1).maybeSingle(),
    ])
    setYaPublicado(activaRes?.data?.ml_item_id ? activaRes.data : null)

    if (!prodRes.data) {
      setMsg({ tipo: 'err', txt: 'Producto no encontrado' })
      setLoading(false)
      return
    }

    const cfg = {}
    for (const r of confRes.data || []) {
      const v = r.value
      cfg[r.key] = typeof v === 'string' ? v.replace(/^"|"$/g, '') : v
    }
    setConfig({
      markup_default:       Number(cfg.ml_markup_default_pct ?? 28),
      comision_pct:         Number(cfg.ml_comision_pct ?? 13.5),
      costo_fijo_rangos:    Array.isArray(cfg.ml_costo_fijo_rangos)
                              ? cfg.ml_costo_fijo_rangos
                              : [{ hasta: 500, fijo: 15 }, { hasta: 750, fijo: 25 }, { hasta: 1000, fijo: 40 }],
      // Bloque fijo institucional oficial = app_config.ml_descripcion_fija (array de 5 strings).
      // La edge fn ml-update-publicacion anexa este mismo array al final antes de mandar a ML.
      // Keys legacy ml_descripcion_header/footer deprecadas (2026-07-13): la edge fn no las lee.
      descripcion_bloques:  Array.isArray(cfg.ml_descripcion_fija)
                              ? cfg.ml_descripcion_fija
                              : (typeof cfg.ml_descripcion_fija === 'string'
                                  ? (() => { try { const p = JSON.parse(cfg.ml_descripcion_fija); return Array.isArray(p) ? p : [] } catch { return [] } })()
                                  : []),
    })

    const prod = prodRes.data
    setProducto(prod)
    setFotos(fotosRes.data || [])
    setTieneConexion(!!credRes.data?.access_token)
    // Si hay draft previo, recuperar su id y los valores guardados
    if (draftRes.data) {
      setPublicacionId(draftRes.data.id)
    }

    // Defaults inteligentes basados en el producto
    const markup = Number(prod.ml_markup_pct ?? cfg.ml_markup_default_pct ?? 28)
    // Regla dura Limpack: precios redondeados hacia arriba (Math.ceil), sin centavos.
    const precioCalculado = prod.ml_precio || Math.ceil(Number(prod.precio_publico || 0) * (1 + markup / 100))

    // Hidratar pub con valores del draft existente (si hay) o defaults del producto
    const draft = draftRes.data
    const draftAttrs = (draft?.attributes || []).reduce((acc, a) => ({ ...acc, [a.id]: a.value_name }), {})
    setPub(p => ({
      ...p,
      categoria_id: draft?.categoria_id || prod.ml_categoria_id || categoriasDePresentacion(prod.nombre),
      titulo:       draft?.titulo || prod.ml_titulo || generarTituloInicial(prod),
      // Si el draft ya tiene family_name confirmado, manda ese. Si no, el efecto de más abajo
      // pre-carga una sugerencia (heredada del hermano o derivada) que Nico puede editar.
      family_name:  draft?.family_name || '',
      descripcion:  draft?.descripcion || prod.ml_descripcion || '',
      precio:       draft?.precio || precioCalculado,
      listing_type: draft?.listing_type || null,
      condition:    draft?.condition || 'new',
      shipping_mode:draft?.shipping_mode || 'me2',
      free_shipping:draft?.free_shipping ?? false,
      attributes:   Object.keys(draftAttrs).length > 0
                      ? draftAttrs
                      : (prod.ml_atributos?.BRAND ? { ...autoAtributos(prod), ...prod.ml_atributos } : autoAtributos(prod)),
      available_quantity: draft?.available_quantity || Math.max(1, Math.floor(Number(prod.stock_actual || 0) - Number(prod.stock_reservado || 0))),
      peso_g:       prod.peso_g || null,
      largo_cm:     prod.largo_cm || null,
      ancho_cm:     prod.ancho_cm || null,
      alto_cm:      prod.alto_cm || null,
    }))

    setLoading(false)
  }
  useEffect(() => { cargar() }, [productoId])

  // Detecta la publicación activa de la misma marca+categoría. ML agrupa esos items como variantes
  // de una misma familia, así que compartirían family_name (= título público). Re-corre si cambia
  // la categoría en el paso 2.
  useEffect(() => {
    let cancelado = false
    async function detectarFamilia() {
      if (!pub.categoria_id || !producto?.marca) { setFamiliaHermano(null); return }
      const { data } = await supabase.from('ml_publicaciones')
        .select('id, titulo, family_name, ml_item_id, producto:productos(marca)')
        .eq('categoria_id', pub.categoria_id).eq('status', 'active')
      const hermano = (data || []).find(h =>
        h.family_name && h.id !== publicacionId && (h.producto?.marca || '') === producto.marca)
      if (!cancelado) setFamiliaHermano(hermano || null)
    }
    detectarFamilia()
    return () => { cancelado = true }
  }, [pub.categoria_id, producto?.marca, publicacionId])

  // Pre-carga la sugerencia de family_name una sola vez (cuando todavía no hay ninguna elegida).
  // Prioriza heredar la del hermano para que ML agrupe bien las variantes; si no hay, deriva del
  // título. En ambos casos el paso 4 lo muestra editable y avisa de dónde salió.
  useEffect(() => {
    if (pub.family_name || !producto || !pub.titulo) return
    const sugerido = familiaHermano?.family_name || derivarFamilyName(pub.titulo, producto.marca)
    if (sugerido) update('family_name', sugerido)
  }, [familiaHermano, producto, pub.titulo, pub.family_name])

  // Auto-detección local por keyword — devuelve categoría HOJA (no padre genérico).
  // Se usa como fallback cuando el predictor de ML todavía no respondió (hidratación
  // inicial del wizard). El paso 2 después llama al domain_discovery/search real y
  // puede overridear la categoría según lo que devuelva ML.
  function categoriasDePresentacion(nombre) {
    const n = (nombre || '').toLowerCase()
    // Papel y desechables
    if (/papel\s*higi[eé]nico|higi[eé]nico/.test(n)) return 'MLU189738' // Papeles Higiénicos
    if (/papel\s*cocina|rollo\s*cocina/.test(n))     return 'MLU388906' // Rollos de Cocina
    if (/servilleta/.test(n))                         return 'MLU413219' // Servilletas de Papel
    if (/bolsa.*basura|bolsa.*residuo/.test(n))      return 'MLU388855' // Bolsas de Basura
    if (/papel\s*aluminio/.test(n))                   return 'MLU413160'
    if (/papel\s*film/.test(n))                       return 'MLU413156'
    // Lavandería — SIEMPRE hojas listables (el padre MLU407832 tiene listing_allowed:false → ML
    // rechaza el POST pidiendo family_name). Suavizante y jabón/detergente de ropa van a su hoja.
    if (/suavizante/.test(n))                                            return 'MLU407838' // Suavizantes para Ropa (hoja)
    if (/jab[oó]n.*ropa|detergente.*ropa|lavarropas|jab[oó]n\s*l[ií]quid/.test(n)) return 'MLU436132' // Jabón para Ropa (hoja)
    // Productos de limpieza específicos
    if (/lavandina|hipoclorito/.test(n))              return 'MLU442584' // Lavandinas
    if (/desengrasante/.test(n))                       return 'MLU442587'
    if (/limpiador.*multiuso|multiuso/.test(n))       return 'MLU442586' // Limpiadores Multiuso
    if (/detergente.*vajilla|lavavajillas?|lavavajilla/.test(n)) return 'MLU189737' // Detergentes
    if (/detergente/.test(n))                          return 'MLU189737'
    if (/aromatizador|ambientador/.test(n))            return 'MLU189736'
    if (/toallita.*desinfectante/.test(n))            return 'MLU417852'
    if (/cera.*piso/.test(n))                          return 'MLU388722'
    if (/desodorante.*piso/.test(n))                   return 'MLU388723'
    if (/desodorante.*(inodoro|ba[nñ]o)/.test(n))    return 'MLU417808'
    if (/destapa.*ca[nñ]er[ií]a/.test(n))            return 'MLU416654'
    if (/limpiador.*alfombra/.test(n))                return 'MLU417311'
    // Accesorios — hojas listables verificadas (04-ago-2026). Antes caían en MLU189726/MLU436100,
    // que son padres NO listables → ML rechazaba el POST al final del wizard.
    if (/esponja|estropajo/.test(n))                  return 'MLU442589' // Esponjas de Limpieza
    if (/guante/.test(n))                             return 'MLU416857' // Guantes de Limpieza
    if (/cepillo/.test(n))                            return 'MLU442591' // Cepillos de Limpieza
    if (/escoba|escobill[oó]n/.test(n))               return 'MLU189733' // Escobas y Escobillones
    if (/trapo|pa[nñ]o|reji|franela/.test(n))         return 'MLU388817' // Trapos y Paños
    if (/broche|pinza.*ropa/.test(n))                 return 'MLU414001' // Broches para Ropa
    if (/canasta.*ropa|cesto/.test(n))                return 'MLU436112' // Cestos de Ropa
    if (/percha/.test(n))                             return 'MLU388187' // Perchas
    // Fallback genérico → hoja LISTABLE (NO el padre MLU442578, que tiene listing_allowed:false).
    // Step2 refina con la predicción real de ML; esto es solo la hidratación inicial.
    return 'MLU442586' // Limpiadores Multiuso (hoja)
  }
  function generarTituloInicial(prod) {
    const partes = [prod.marca, prod.nombre, prod.presentacion].filter(Boolean)
    return partes.join(' ').slice(0, 60)
  }
  function generarDescripcionInicial(prod) {
    return [
      prod.descripcion || `${prod.nombre} en presentación ${prod.presentacion || ''}.`,
      '',
      'Sobre Limpack: empresa uruguaya que selecciona productos de limpieza para tu hogar. Productos chequeados, mejor que el promedio del mercado. RUT 220.903.660.013 · Montevideo, Uruguay.',
      '',
      '⚡ Despacho rápido en Montevideo con Mercado Envíos Flex.',
    ].join('\n').trim()
  }

  function update(k, v) { setPub(p => ({ ...p, [k]: v })) }
  function updateAttr(k, v) { setPub(p => ({ ...p, attributes: { ...p.attributes, [k]: v } })) }

  async function subirFoto(file, contexto = null) {
    if (!file) return
    // Validación de archivo: peso (ML corta ~10 MB) y resolución (ML recomienda ≥1200px; solo avisa).
    if (file.size > 10 * 1024 * 1024) {
      setMsg({ tipo: 'err', txt: `La foto pesa ${(file.size / 1024 / 1024).toFixed(1)} MB (máx 10 MB). Reducila y volvé a subir.` })
      return
    }
    try {
      const dims = await new Promise((res) => {
        const objUrl = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => { res({ w: img.naturalWidth, h: img.naturalHeight }); URL.revokeObjectURL(objUrl) }
        img.onerror = () => { res(null); URL.revokeObjectURL(objUrl) }
        img.src = objUrl
      })
      if (dims && Math.min(dims.w, dims.h) < 1200) {
        setMsg({ tipo: 'err', txt: `⚠ Foto de ${dims.w}×${dims.h}px. ML recomienda mínimo 1200px por lado para que permita zoom y no se vea borrosa. Podés subirla igual, pero conviene una más grande.` })
      }
    } catch (_) { /* si no se pudo medir, seguimos */ }
    const ext = file.name.split('.').pop()
    // Si es foto_original: la ponemos al principio para que el orquestador la use como base
    const prefijo = contexto === 'foto_original' ? 'original-real' : 'manual'
    const nombreArchivo = `${productoId}/${prefijo}-${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage
      .from('productos-fotos-ml')
      .upload(nombreArchivo, file, { upsert: false, contentType: file.type })
    if (upErr) {
      setMsg({ tipo: 'err', txt: 'Error subiendo foto: ' + upErr.message })
      return
    }
    const proxPosicion = proximaPosicion()
    const esPortada = faltaPortada()
    const { data: row, error: insErr } = await supabase.from('ml_fotos').insert({
      producto_id: productoId,
      posicion: proxPosicion,
      tipo: 'base',
      contexto,   // 'foto_original' cuando viene del botón "Subir foto real"
      es_portada: esPortada,
      storage_bucket: 'productos-fotos-ml',
      storage_path: nombreArchivo,
      bytes: file.size,
    }).select().single()
    if (insErr) { setMsg({ tipo: 'err', txt: insErr.message }); return }
    setFotos(f => [...f, row])
    if (contexto === 'foto_original') {
      setMsg({ tipo: 'ok', txt: '✓ Foto real cargada. Ahora las IA la usarán como base (no la Flux de tienda).' })
    }
  }

  // Importar la foto que ya tiene en limpack.uy como foto base
  async function importarFotoLimpack() {
    if (!producto?.imagen_url) {
      setMsg({ tipo: 'err', txt: 'El producto no tiene imagen_url en limpack.uy' })
      return
    }
    try {
      // Descargar la imagen
      const resp = await fetch(producto.imagen_url)
      if (!resp.ok) throw new Error('No pude descargar la imagen original')
      const blob = await resp.blob()
      const ext = (producto.imagen_url.split('.').pop() || 'jpg').toLowerCase().replace(/\?.*$/, '')
      const nombreArchivo = `${productoId}/base-limpack-${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage
        .from('productos-fotos-ml')
        .upload(nombreArchivo, blob, { upsert: false, contentType: blob.type || 'image/jpeg' })
      if (upErr) throw upErr
      const esPortada = faltaPortada()
      const { data: row, error: insErr } = await supabase.from('ml_fotos').insert({
        producto_id: productoId,
        posicion: proximaPosicion(),
        tipo: 'base',
        contexto: 'foto_limpack_uy',
        es_portada: esPortada,
        storage_bucket: 'productos-fotos-ml',
        storage_path: nombreArchivo,
        bytes: blob.size,
      }).select().single()
      if (insErr) throw insErr
      setFotos(f => [...f, row])
      setMsg({ tipo: 'ok', txt: 'Foto de limpack.uy importada como base. Ahora podés generar variantes con IA.' })
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error importando: ' + e.message })
    }
  }

  // Close-up SEGURO: crop centrado de la foto base (no genera con IA, recorta la original).
  // Esto evita que Flux invente texto en la etiqueta.
  async function generarCloseUpSeguro(fotoBase) {
    const { data: signed } = await supabase.storage.from(fotoBase.storage_bucket).createSignedUrl(fotoBase.storage_path, 600)
    if (!signed?.signedUrl) throw new Error('No pude leer la foto base')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    await new Promise((res, rej) => {
      img.onload = res
      img.onerror = () => rej(new Error('No cargué la imagen base'))
      img.src = signed.signedUrl
    })
    // Crop 55% centrado (zoom etiqueta), 1200x1200 cuadrado
    const sw = Math.round(img.naturalWidth * 0.55)
    const sh = Math.round(img.naturalHeight * 0.55)
    const sx = Math.round((img.naturalWidth - sw) / 2)
    const sy = Math.round((img.naturalHeight - sh) / 2)
    const canvas = document.createElement('canvas')
    canvas.width = 1200
    canvas.height = 1200
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, 1200, 1200)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 1200, 1200)
    const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.92))
    if (!blob) throw new Error('No pude generar el JPG del close-up')
    const filename = `${productoId}/crop-close_up-${Date.now()}.jpg`
    const { error: upErr } = await supabase.storage.from('productos-fotos-ml').upload(filename, blob, { contentType: 'image/jpeg', upsert: false })
    if (upErr) throw upErr
    const { data: row, error: insErr } = await supabase.from('ml_fotos').insert({
      producto_id: productoId,
      posicion: proximaPosicion(),
      tipo: 'crop',
      contexto: 'close_up',
      es_portada: false,
      storage_bucket: 'productos-fotos-ml',
      storage_path: filename,
      bytes: blob.size,
      generation_model: 'canvas-crop-55pct-centered',
      generation_cost_usd: 0,
    }).select().single()
    if (insErr) throw insErr
    setFotos(f => [...f, row])
    return row
  }

  // Generar variantes IA con Replicate Flux (o crop seguro para close-up)
  async function generarVariantesIA(contextos) {
    if (fotos.length === 0) {
      setMsg({ tipo: 'err', txt: 'Necesitás al menos 1 foto base antes de generar variantes' })
      return
    }
    const fotoBase = fotos.find(f => f.tipo === 'base') || fotos[0]
    try {
      // Interceptar close_up: en lugar de Flux (riesgo de inventar etiqueta),
      // hacer un crop centrado de la foto real. Mismo resultado visual, etiqueta 100% intacta.
      const contextosFlux = contextos.filter(c => c !== 'close_up')
      const haceCloseUp = contextos.includes('close_up')
      let creadas = 0
      let costo = 0

      if (haceCloseUp) {
        try {
          await generarCloseUpSeguro(fotoBase)
          creadas++
        } catch (e) {
          setMsg({ tipo: 'err', txt: 'close-up: ' + e.message })
        }
      }

      if (contextosFlux.length > 0) {
        const { data, error } = await supabase.functions.invoke('ml-generar-fotos-ia', {
          body: { producto_id: productoId, foto_base_id: fotoBase.id, contextos: contextosFlux }
        })
        if (error || data?.error) {
          setMsg({ tipo: 'err', txt: 'IA fotos: ' + (error?.message || data?.error) })
          return
        }
        const nuevasFotos = data?.fotos_generadas || []
        setFotos(f => [...f, ...nuevasFotos])
        creadas += nuevasFotos.length
        costo += data?.costo_total_usd || 0
      }

      setMsg({ tipo: 'ok', txt: `${creadas} variantes generadas (USD $${costo.toFixed(3)})` })
    } catch (e) {
      setMsg({ tipo: 'err', txt: e.message })
    }
  }

  // Orquestador: arma la galería automática de un producto
  // Pasos: (1) importar foto limpack.uy como lifestyle, (2) bg-remover → portada fondo blanco,
  // (3) Flux Kontext → vista cenital. Las 2 placas fijas se anexan solas al final via FotosFijasCandado.
  const [armandoAuto, setArmandoAuto] = useState(false)
  async function armarGaleriaAutomatica() {
    if (armandoAuto) return
    setArmandoAuto(true)
    setMsg(null)
    try {
      // Paso 1: elegir foto base para las IA
      // PRIORIDAD: (1) foto real subida por Nico > (2) Flux de limpack.uy legacy
      // La real preserva las etiquetas correctamente; la Flux tiene texto inventado.
      let fotoBase = fotos.find(f => f.tipo === 'base' && f.contexto === 'foto_original')
                  || fotos.find(f => f.tipo === 'base' && f.contexto === 'foto_limpack_uy')
      if (!fotoBase) {
        if (!producto?.imagen_url) {
          setMsg({ tipo: 'err', txt: 'El producto no tiene imagen_url en limpack.uy. Importá una foto manual primero.' })
          setArmandoAuto(false)
          return
        }
        setMsg({ tipo: 'ok', txt: '1/3 · Importando foto lifestyle de limpack.uy…' })
        const resp = await fetch(producto.imagen_url)
        if (!resp.ok) throw new Error('No pude descargar la imagen de limpack.uy')
        const blob = await resp.blob()
        const ext = (producto.imagen_url.split('.').pop() || 'jpg').toLowerCase().replace(/\?.*$/, '')
        const nombreArchivo = `${productoId}/base-limpack-${Date.now()}.${ext}`
        const { error: upErr } = await supabase.storage
          .from('productos-fotos-ml')
          .upload(nombreArchivo, blob, { upsert: false, contentType: blob.type || 'image/jpeg' })
        if (upErr) throw upErr
        const { data: row, error: insErr } = await supabase.from('ml_fotos').insert({
          producto_id: productoId,
          posicion: proximaPosicion(),
          tipo: 'base',
          contexto: 'foto_limpack_uy',
          es_portada: false,
          storage_bucket: 'productos-fotos-ml',
          storage_path: nombreArchivo,
          bytes: blob.size,
        }).select().single()
        if (insErr) throw insErr
        fotoBase = row
        setFotos(f => [...f, row])
      }

      // Paso 2 y 3: generar SÓLO los contextos que faltan
      const tienePortadaBlanco = fotos.some(f => f.contexto === 'fondo_blanco')
      const tieneCenital = fotos.some(f => f.contexto === 'cenital')
      const contextosFaltantes = []
      if (!tienePortadaBlanco) contextosFaltantes.push('fondo_blanco')
      if (!tieneCenital) contextosFaltantes.push('cenital')

      let nuevasFotos = []
      let costoTotal = 0
      if (contextosFaltantes.length > 0) {
        setMsg({ tipo: 'ok', txt: `Generando: ${contextosFaltantes.join(' + ')}…` })
        const { data, error } = await supabase.functions.invoke('ml-generar-fotos-ia', {
          body: {
            producto_id: productoId,
            foto_base_id: fotoBase.id,
            contextos: contextosFaltantes,
          }
        })
        if (error || data?.error) throw new Error(error?.message || data?.error || 'Error en ml-generar-fotos-ia')
        nuevasFotos = data?.fotos_generadas || []
        costoTotal = data?.costo_total_usd || 0
        setFotos(f => [...f, ...nuevasFotos])
      }

      // Marcar la "fondo blanco" como portada (si la generamos o si ya existía)
      const portada = nuevasFotos.find(f => f.contexto === 'fondo_blanco')
                  || fotos.find(f => f.contexto === 'fondo_blanco')
      if (portada) {
        await supabase.from('ml_fotos').update({ es_portada: false }).eq('producto_id', productoId)
        await supabase.from('ml_fotos').update({ es_portada: true }).eq('id', portada.id)
        setFotos(f => f.map(x => ({ ...x, es_portada: x.id === portada.id })))
      }

      setMsg({
        tipo: 'ok',
        txt: contextosFaltantes.length > 0
          ? `✓ Listo. ${nuevasFotos.length} fotos nuevas + 2 placas Limpack al final. Costo: USD $${costoTotal.toFixed(3)}`
          : '✓ Ya tenías todo. Las 2 placas Limpack se anexan al publicar.'
      })
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Auto galería: ' + (e?.message || String(e)) })
    } finally {
      setArmandoAuto(false)
    }
  }

  async function eliminarFoto(foto) {
    if (!confirm('¿Eliminar esta foto?')) return
    await supabase.storage.from('productos-fotos-ml').remove([foto.storage_path])
    await supabase.from('ml_fotos').delete().eq('id', foto.id)
    setFotos(f => f.filter(x => x.id !== foto.id))
  }

  async function marcarPortada(foto) {
    // Marcar como portada Y mover a posición 0 (ML usa la primera como portada)
    const ordenadas = [foto, ...fotos.filter(f => f.id !== foto.id)]
    await supabase.from('ml_fotos').update({ es_portada: false }).eq('producto_id', productoId)
    await supabase.from('ml_fotos').update({ es_portada: true }).eq('id', foto.id)
    // Reasignar posiciones
    for (let i = 0; i < ordenadas.length; i++) {
      await supabase.from('ml_fotos').update({ posicion: i }).eq('id', ordenadas[i].id)
    }
    setFotos(ordenadas.map((x, i) => ({ ...x, posicion: i, es_portada: x.id === foto.id })))
  }

  async function moverFoto(foto, direccion) {
    // direccion: -1 = atrás (hacia portada), +1 = adelante
    const idx = fotos.findIndex(f => f.id === foto.id)
    if (idx < 0) return
    const nuevoIdx = idx + direccion
    if (nuevoIdx < 0 || nuevoIdx >= fotos.length) return
    const ordenadas = [...fotos]
    const tmp = ordenadas[idx]
    ordenadas[idx] = ordenadas[nuevoIdx]
    ordenadas[nuevoIdx] = tmp
    // Actualizar posiciones en DB
    await Promise.all([
      supabase.from('ml_fotos').update({ posicion: nuevoIdx }).eq('id', foto.id),
      supabase.from('ml_fotos').update({ posicion: idx }).eq('id', ordenadas[idx].id),
    ])
    // Recalcular es_portada (la primera en posición 0)
    const primera = ordenadas[0]
    if (primera && !primera.es_portada) {
      await supabase.from('ml_fotos').update({ es_portada: false }).eq('producto_id', productoId)
      await supabase.from('ml_fotos').update({ es_portada: true }).eq('id', primera.id)
      ordenadas[0] = { ...primera, es_portada: true }
      for (let i = 1; i < ordenadas.length; i++) ordenadas[i] = { ...ordenadas[i], es_portada: false }
    }
    setFotos(ordenadas.map((x, i) => ({ ...x, posicion: i })))
  }

  // Estado auxiliar: cuando guardarDraft falla en modo silencioso, el caller
  // (publicarEnML / previsualizarML) necesita el mensaje real para no mostrar
  // el falso "faltan categoría/título".
  async function guardarDraft(silencioso = false) {
    if (!silencioso) setSaving(true)
    if (!silencioso) setMsg(null)
    // Construir payload defensivo: si falta el mínimo (titulo, categoria), no inserta
    if (!pub.categoria_id || !pub.titulo) {
      if (!silencioso) {
        setMsg({ tipo: 'err', txt: 'Faltan categoría y título mínimos para guardar' })
        setSaving(false)
      }
      guardarDraft._ultimoError = 'faltan categoría o título'
      return
    }
    const payload = {
      producto_id: productoId,
      categoria_id: pub.categoria_id,
      titulo: pub.titulo,
      // Lo que Nico dejó en el paso 4 es la verdad: la edge fn publica ESTO, no el heurístico.
      family_name: (pub.family_name || '').trim() || null,
      descripcion: pub.descripcion || '',
      // Regla dura: Math.ceil, sin centavos. NO forzar 0→1 en silencio (un $1 publicado es un regalo);
      // publicarEnML bloquea si el precio no es válido.
      precio: Math.ceil(Number(pub.precio) || 0),
      currency_id: 'UYU',
      // El draft puede guardarse sin tipo elegido; publicar sí lo exige (ver publicarEnML).
      listing_type: pub.listing_type || null,
      condition: pub.condition || 'new',
      available_quantity: Number(pub.available_quantity || 1) || 1,
      attributes: Object.entries(pub.attributes || {})
        .filter(([_, v]) => v && String(v).trim())
        .map(([id, value_name]) => ({ id, value_name })),
      shipping_mode: pub.shipping_mode || 'me2',
      free_shipping: pub.free_shipping || false,
      status: 'draft',
      updated_at: new Date().toISOString(),
    }
    let error
    let nuevoId = publicacionId
    if (publicacionId) {
      // UPDATE del draft existente
      const r = await supabase.from('ml_publicaciones').update(payload).eq('id', publicacionId)
      error = r.error
    } else {
      // INSERT nuevo y guardar el id
      const r = await supabase.from('ml_publicaciones').insert(payload).select().single()
      error = r.error
      if (r.data) { setPublicacionId(r.data.id); nuevoId = r.data.id }
    }
    if (error) {
      // Guardar el error para el caller aunque estemos en silencioso.
      guardarDraft._ultimoError = error.message || 'error desconocido guardando el draft'
      if (!silencioso) { setMsg({ tipo: 'err', txt: error.message }); setSaving(false) }
      console.error('[guardarDraft] error supabase:', error)
      return null
    }
    guardarDraft._ultimoError = null

    // También actualizar productos: info ML + dimensiones.
    // Fix 15-jul-2026: antes se tragaba el error del segundo update; ahora si falla,
    // avisamos al caller para que el edge fn NO se dispare con data sucia.
    const rProd = await supabase.from('productos').update({
      ml_categoria_id: pub.categoria_id,
      ml_titulo: pub.titulo,
      ml_descripcion: pub.descripcion,
      ml_atributos: pub.attributes,
      ml_listing_type: pub.listing_type,
      ml_precio: Number(pub.precio || 0),
      ml_status: 'draft',
      peso_g:   pub.peso_g   || null,
      largo_cm: pub.largo_cm || null,
      ancho_cm: pub.ancho_cm || null,
      alto_cm:  pub.alto_cm  || null,
    }).eq('id', productoId)
    if (rProd.error) {
      console.error('[guardarDraft] error UPDATE productos (segundo update):', rProd.error)
      guardarDraft._ultimoError = 'update productos: ' + rProd.error.message
      if (!silencioso) { setMsg({ tipo: 'err', txt: 'Error guardando en productos: ' + rProd.error.message }); setSaving(false) }
      return null
    }

    if (silencioso) {
      setAutoSaved(new Date())
    } else {
      setMsg({ tipo: 'ok', txt: 'Borrador guardado. Cuando se conecte ML, lo publicás con 1 click.' })
      setSaving(false)
    }
    return nuevoId
  }

  // Publicación REAL a ML: guarda el draft y llama a la edge fn ml-publicar-producto.
  async function publicarEnML() {
    setSaving(true)
    setMsg(null)
    // Guardia anti-duplicado: si este producto YA tiene un ítem activo/pausado en ML, publicar de
    // nuevo crea un listing duplicado que ML modera. Pedir confirmación explícita.
    if (yaPublicado?.ml_item_id) {
      const seguir = window.confirm(
        `⚠ Este producto YA está publicado en ML (${yaPublicado.ml_item_id}, ${yaPublicado.status}).\n\nPublicar de nuevo crea un DUPLICADO que ML puede moderar. Para cambiar la publicación existente, usá "actualizar" desde la lista de MercadoLibre.\n\n¿Crear igual una publicación nueva?`
      )
      if (!seguir) { setMsg({ tipo: 'err', txt: `Cancelado. Ya existe la publicación ${yaPublicado.ml_item_id}.` }); setSaving(false); return }
    }
    // Precio válido: sin esto un draft en 0 saldría como $1 (regalo). Regla dura: Math.ceil.
    if (!(Math.ceil(Number(pub.precio) || 0) >= 1)) {
      setMsg({ tipo: 'err', txt: 'Cargá un precio válido en el paso 7 antes de publicar.' })
      setStep(7)
      setSaving(false); return
    }
    // Al menos 1 foto del producto (las placas fijas solas no alcanzan como ficha).
    if (fotos.length === 0) {
      setMsg({ tipo: 'err', txt: 'Subí al menos 1 foto del producto en el paso 6 antes de publicar.' })
      setStep(6)
      setSaving(false); return
    }
    // Dimensiones de envío obligatorias: sin ellas ML aplica el default 9799 g / 119 cm y el flete se
    // dispara (bug del 15-jul, lavandina 1L con flete $2500). Bloquear, no solo avisar.
    const dimsFaltan = [
      ['peso_g', 'peso (g)'], ['largo_cm', 'largo (cm)'], ['ancho_cm', 'ancho (cm)'], ['alto_cm', 'alto (cm)'],
    ].filter(([k]) => !(Number(pub[k]) > 0)).map(([, lbl]) => lbl)
    if (dimsFaltan.length) {
      setMsg({ tipo: 'err', txt: `Faltan dimensiones en el paso 7: ${dimsFaltan.join(', ')}. Sin ellas ML infla el flete (default 9799 g / 119 cm).` })
      setStep(7)
      setSaving(false); return
    }
    // El tipo de publicación se elige por producto y no tiene default: sin él no se publica.
    // Además blinda contra listing types que no existen en MLU (ver LISTING_TYPES).
    if (!pub.listing_type || !LISTING_TYPES.some(t => t.id === pub.listing_type)) {
      setMsg({ tipo: 'err', txt: 'Elegí el tipo de publicación en el paso 8 antes de publicar.' })
      setStep(8)
      setSaving(false); return
    }
    // family_name es el título público y ML lo exige (pipeline User Products): sin él no hay POST.
    if (!(pub.family_name || '').trim()) {
      setMsg({ tipo: 'err', txt: 'Completá el nombre de familia en el paso 4: es el título que ve el comprador.' })
      setStep(4)
      setSaving(false); return
    }
    const pubId = await guardarDraft(true)  // asegura ml_publicaciones al día y devuelve el id
    if (!pubId) {
      const detalle = guardarDraft._ultimoError || 'faltan categoría/título'
      setMsg({ tipo: 'err', txt: 'No se pudo guardar el borrador: ' + detalle })
      setSaving(false); return
    }
    if (!tieneConexion) {
      setMsg({ tipo: 'ok', txt: 'Guardado como draft. Conectá la cuenta ML para publicar.' })
      setSaving(false); return
    }
    // Validación pre-envío: ML acepta el POST y DESCARTA en silencio los atributos con id
    // desconocido. Chequeamos contra la categoría real ANTES de publicar y avisamos.
    try {
      const catAttrs = await fetchCategoriaAttrs(pub.categoria_id)
      const validIds = new Set(catAttrs.map(a => a.id))
      // BLOQUEO: atributos OBLIGATORIOS de ML sin completar. Antes solo se avisaba ("mejorá el score")
      // y el POST fallaba recién en ML. BRAND lo agrega la edge desde productos.marca, así que se
      // exceptúa si el producto tiene marca. Los conditional_required quedan como aviso, no bloqueo.
      const requiredFaltantes = catAttrs
        .filter(a => { const t = a.tags || {}; return (t.required || t.catalog_required) && !t.hidden && !t.read_only && !t.fixed })
        .filter(a => !(a.id === 'BRAND' && producto?.marca))
        .filter(a => !String((pub.attributes || {})[a.id] ?? '').trim())
      if (requiredFaltantes.length > 0) {
        setMsg({ tipo: 'err', txt: `Faltan atributos obligatorios de ML (paso 3): ${requiredFaltantes.map(a => a.name).join(', ')}.` })
        setStep(3); setSaving(false); return
      }
      const invalidos = Object.entries(pub.attributes || {})
        .filter(([id, v]) => v && String(v).trim() && !validIds.has(id))
        .map(([id]) => id)
      if (invalidos.length > 0) {
        const seguir = window.confirm(
          `⚠ Estos atributos NO van a subir (ML no los reconoce en esta categoría y los descarta):\n\n${invalidos.join(', ')}\n\n¿Publicar igual sin ellos? (Cancelá para corregirlos en el paso 3)`
        )
        if (!seguir) { setMsg({ tipo: 'err', txt: `Publicación cancelada. Corregí en el paso 3: ${invalidos.join(', ')}` }); setSaving(false); return }
      }
    } catch (_) { /* si no se pudo validar, seguimos — el backend igual filtra */ }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-publicar-producto`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ publicacion_id: pubId }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data.error) {
        const detalle = data.ml_error?.cause?.map?.((c) => c.message).join(' · ') || data.ml_error?.message || data.error || `HTTP ${resp.status}`
        setMsg({ tipo: 'err', txt: 'ML rechazó la publicación: ' + detalle })
        setSaving(false); return
      }
      const avisos = [
        data.fotos_errores?.length ? `⚠ ${data.fotos_errores.length} foto(s) con error` : '',
        data.attrs_descartados?.length ? `⚠ atributos no subidos: ${data.attrs_descartados.join(', ')}` : '',
        data.persistido === false ? '⚠ NO se guardó en la BD (avisá a soporte)' : '',
      ].filter(Boolean)
      setMsg({ tipo: data.persistido === false ? 'err' : 'ok', txt: `¡Publicado en ML! Item ${data.ml_item_id}${avisos.length ? ' · ' + avisos.join(' · ') : ''}` })
      setSaving(false)
      if (data.permalink) window.open(data.permalink, '_blank', 'noopener')
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error de red al publicar: ' + e.message })
      setSaving(false)
    }
  }

  // Dry-run: arma el payload en el backend SIN publicar y lo muestra en Step10.
  async function previsualizarML() {
    setSaving(true)
    setMsg(null)
    setPreviewPayload(null)
    const pubId = await guardarDraft(true)
    if (!pubId) {
      const detalle = guardarDraft._ultimoError || 'faltan categoría/título'
      setMsg({ tipo: 'err', txt: 'No se pudo guardar el borrador: ' + detalle })
      setSaving(false); return
    }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-publicar-producto`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ publicacion_id: pubId, dry_run: true }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data.error) { setMsg({ tipo: 'err', txt: 'Dry-run: ' + (data.error || `HTTP ${resp.status}`) }); setSaving(false); return }
      setPreviewPayload(data)
      setSaving(false)
    } catch (e) {
      setMsg({ tipo: 'err', txt: 'Error de red en dry-run: ' + e.message })
      setSaving(false)
    }
  }

  // Auto-save al cambiar de paso (silencioso)
  async function cambiarPaso(nuevoStep) {
    await guardarDraft(true)
    setStep(nuevoStep)
  }

  function fmtRel(d) {
    if (!d) return ''
    const sec = (Date.now() - new Date(d).getTime()) / 1000
    if (sec < 60) return `hace ${Math.round(sec)}s`
    if (sec < 3600) return `hace ${Math.round(sec/60)} min`
    return `hace ${Math.round(sec/3600)}h`
  }

  if (loading) return <AdminLayout title="Publicar en ML"><div style={{ padding: 40, textAlign: 'center', color: '#888780' }}>Cargando…</div></AdminLayout>
  if (!producto) return <AdminLayout title="Publicar en ML"><div style={{ padding: 40, textAlign: 'center', color: '#C53030' }}>Producto no encontrado</div></AdminLayout>

  // Comisión real según el tipo elegido en el paso 8 — se recalcula sola al cambiarlo.
  // Mientras no haya tipo elegido, `conocido: false` y el paso 7 no muestra un neto inventado.
  const precioActual = Number(pub.precio || 0)
  const feeML = calcularComisionML(precioActual, pub.listing_type, config.costo_fijo_rangos)
  const neto = precioActual - feeML.total
  const dif = neto - Number(producto.precio_publico || 0)

  return (
    <AdminLayout title={`Publicar: ${producto.nombre}`}>
      <div className="wz-wrap">
        <header className="wz-head">
          <div>
            <div className="wz-eyebrow">
              <Link to="/admin/canales/mercadolibre">← MercadoLibre</Link> · Publicar producto
            </div>
            <h1 className="wz-title">{producto.nombre}</h1>
            <div className="wz-sub">{producto.sku} · {producto.marca} · {producto.presentacion}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
            {!tieneConexion && (
              <span className="wz-pill warn">⚠ ML sin conectar · guardás como draft</span>
            )}
            {autoSaved && (
              <span style={{ fontSize: 11, color: '#1D9E75', fontWeight: 600 }}>
                ✓ Auto-guardado {fmtRel(autoSaved)}
              </span>
            )}
            {!autoSaved && publicacionId && (
              <span style={{ fontSize: 11, color: '#5F5E5A' }}>
                Borrador del {producto?.ml_status === 'draft' ? 'producto recuperado' : 'producto'}
              </span>
            )}
          </div>
        </header>

        {msg && <div className={`wz-msg ${msg.tipo}`} onClick={() => setMsg(null)}>{msg.txt}<span style={{ float: 'right', cursor: 'pointer' }}>×</span></div>}

        {yaPublicado?.ml_item_id && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12.5, color: '#78350F', lineHeight: 1.55 }}>
            ⚠ Este producto <strong>ya está publicado en ML</strong> ({yaPublicado.status} · <code>{yaPublicado.ml_item_id}</code>
            {yaPublicado.permalink && <> · <a href={yaPublicado.permalink} target="_blank" rel="noopener" style={{ color: '#00A8B5' }}>ver</a></>}).
            Publicar de nuevo crea un <strong>duplicado</strong> que ML modera. Para cambiar la ficha existente, actualizá esa publicación desde la lista de MercadoLibre.
          </div>
        )}

        <div className="wz-layout">
          <div className="wz-steps">
            <h4>Pasos</h4>
            {STEPS.map(s => (
              <div
                key={s.id}
                className={`wz-step ${s.id === step ? 'active' : ''} ${s.id < step ? 'done' : ''}`}
                onClick={() => cambiarPaso(s.id)}
              >
                <span className="num">{s.id < step ? '✓' : s.id}</span>
                <div className="wz-step-lbl">
                  {s.label}
                  <div className="wz-step-sub">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="wz-body">
            {step === 1 && <Step1 producto={producto} />}
            {step === 2 && <Step2 pub={pub} update={update} />}
            {step === 3 && <Step3 pub={pub} updateAttr={updateAttr} prod={producto} />}
            {step === 4 && <Step4 pub={pub} update={update} producto={producto} familiaHermano={familiaHermano} />}
            {step === 5 && <Step5 pub={pub} update={update} config={config} productoId={productoId} setMsg={setMsg} />}
            {step === 6 && <Step6 producto={producto} fotos={fotos} onSubir={subirFoto} onEliminar={eliminarFoto} onPortada={marcarPortada} onImportarLimpack={importarFotoLimpack} onGenerarIA={generarVariantesIA} onAbrirGrande={setFotoGrande} onArmarAuto={armarGaleriaAutomatica} armandoAuto={armandoAuto} onMover={moverFoto} />}
            {step === 7 && <Step7 producto={producto} pub={pub} update={update} config={config} feeML={feeML} neto={neto} dif={dif} />}
            {step === 8 && <Step8 pub={pub} update={update} />}
            {step === 9 && <Step9 pub={pub} update={update} />}
            {step === 10 && <Step10 producto={producto} pub={pub} fotos={fotos} neto={neto} guardarDraft={guardarDraft} saving={saving} tieneConexion={tieneConexion} onPreview={previsualizarML} preview={previewPayload} />}

            <div className="wz-foot">
              <button onClick={() => cambiarPaso(Math.max(1, step - 1))} disabled={step === 1} className="wz-btn-ghost">← Anterior</button>
              <div style={{ flex: 1 }} />
              <span className="wz-step-counter">Paso {step} de {STEPS.length}</span>
              <div style={{ flex: 1 }} />
              {step < STEPS.length ? (
                <button onClick={() => cambiarPaso(Math.min(STEPS.length, step + 1))} className="wz-btn-primary">Siguiente →</button>
              ) : (
                <button onClick={publicarEnML} disabled={saving} className="wz-btn-primary">
                  {saving ? (tieneConexion ? 'Publicando…' : 'Guardando…') : tieneConexion ? '⬆ Publicar en ML' : '💾 Guardar como draft'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {fotoGrande && <Lightbox foto={fotoGrande} onClose={() => setFotoGrande(null)} />}
      <Estilos />
    </AdminLayout>
  )
}

// ─────────────── PASOS ───────────────

function Step1({ producto }) {
  return (
    <>
      <h2>1 · Producto seleccionado</h2>
      <div className="wz-step-desc">Este es el producto del catálogo Limpack que vas a publicar en ML.</div>
      <div className="wz-card">
        <div className="wz-prod-row"><span>SKU</span><strong>{producto.sku}</strong></div>
        <div className="wz-prod-row"><span>Nombre</span><strong>{producto.nombre}</strong></div>
        <div className="wz-prod-row"><span>Marca</span><strong>{producto.marca || '—'}</strong></div>
        <div className="wz-prod-row"><span>Presentación</span><strong>{producto.presentacion || '—'}</strong></div>
        <div className="wz-prod-row"><span>Precio web</span><strong>$ {Number(producto.precio_publico || 0).toLocaleString('es-UY')}</strong></div>
        <div className="wz-prod-row"><span>Stock disponible</span><strong>{Math.max(0, Number(producto.stock_actual || 0) - Number(producto.stock_reservado || 0))} unidades</strong></div>
        <div className="wz-prod-row"><span>Categoría interna</span><strong>{producto.categoria || '—'} · Tier {producto.tier || '—'}</strong></div>
      </div>
    </>
  )
}

function Step2({ pub, update }) {
  const [sugerencias, setSugerencias] = useState([])
  const [cargandoPred, setCargandoPred] = useState(false)
  const [errorPred, setErrorPred] = useState(null)
  const [verTodas, setVerTodas] = useState(false)
  // 15-jul-2026: buscador libre. Filtra la lista interna, busca en la API de ML por texto,
  // y acepta pegar MLU-id directo. Nico se cansó de depender del predictor del título.
  const [busqueda, setBusqueda] = useState('')
  const [busquedaAPI, setBusquedaAPI] = useState([])
  const [cargandoBusquedaAPI, setCargandoBusquedaAPI] = useState(false)
  const [catManualInfo, setCatManualInfo] = useState(null) // {id, name} si pegó un MLU-id válido

  // Buscar en ML API por el texto tipeado (además de filtrar la lista interna)
  useEffect(() => {
    const q = busqueda.trim()
    if (q.length < 3) { setBusquedaAPI([]); setCatManualInfo(null); return }
    // Si es un MLU-id, resolverlo directo a /categories/{id}
    const mluMatch = q.toUpperCase().match(/^(MLU\d+)$/)
    if (mluMatch) {
      const cid = mluMatch[1]
      setCargandoBusquedaAPI(true)
      fetch(`https://api.mercadolibre.com/categories/${cid}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.id) setCatManualInfo({ id: data.id, name: data.name, listing_allowed: data?.settings?.listing_allowed })
          else setCatManualInfo({ id: cid, name: '(no encontrada)' })
        })
        .catch(() => setCatManualInfo({ id: cid, name: '(error consultando)' }))
        .finally(() => setCargandoBusquedaAPI(false))
      setBusquedaAPI([])
      return
    }
    setCatManualInfo(null)
    // Búsqueda libre en el predictor de ML con el texto tipeado
    setCargandoBusquedaAPI(true)
    const ctrl = new AbortController()
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://api.mercadolibre.com/sites/MLU/domain_discovery/search?limit=8&q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal }
        )
        if (!r.ok) throw new Error(`ML ${r.status}`)
        const data = await r.json()
        const seen = new Set()
        const list = []
        for (const it of (data || [])) {
          if (!it.category_id || seen.has(it.category_id)) continue
          seen.add(it.category_id)
          list.push({ id: it.category_id, label: it.category_name, domain_name: it.domain_name })
        }
        setBusquedaAPI(list)
      } catch (e) {
        if (e.name !== 'AbortError') setBusquedaAPI([])
      } finally { setCargandoBusquedaAPI(false) }
    }, 400)
    return () => { clearTimeout(t); ctrl.abort() }
  }, [busqueda])

  // Filtrar la lista interna por el texto del buscador
  const catalogoFiltrado = busqueda.trim().length >= 2
    ? CATEGORIAS_LIMPIEZA.filter(c => {
        const q = busqueda.trim().toLowerCase()
        return c.label.toLowerCase().includes(q) || c.id.toLowerCase().includes(q)
      })
    : []

  // Llamar al domain_discovery/search de ML con el título del producto.
  // Es endpoint público, no requiere token OAuth. Devuelve top 3-5 categorías
  // hoja con dominio + attributes que ML espera. Refresca cuando cambia el título.
  useEffect(() => {
    const q = (pub.titulo || '').trim()
    if (q.length < 3) { setSugerencias([]); return }
    setCargandoPred(true)
    setErrorPred(null)
    const ctrl = new AbortController()
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://api.mercadolibre.com/sites/MLU/domain_discovery/search?limit=5&q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal }
        )
        if (!r.ok) throw new Error(`ML ${r.status}`)
        const data = await r.json()
        // Deduplicar por category_id
        const seen = new Set()
        const list = []
        for (const it of (data || [])) {
          if (!it.category_id || seen.has(it.category_id)) continue
          seen.add(it.category_id)
          list.push({
            id: it.category_id,
            label: it.category_name,
            domain_name: it.domain_name,
            fuente: 'ml_predictor',
          })
        }
        setSugerencias(list)
        // Si el usuario todavía no eligió nada (o eligió el default hardcoded),
        // sugerir la primera opción de ML automáticamente.
        if (list.length > 0 && (!pub.categoria_id || pub.categoria_id === 'MLU442578')) {
          update('categoria_id', list[0].id)
        }
      } catch (e) {
        if (e.name === 'AbortError') return
        setErrorPred(e.message)
        setSugerencias([])
      } finally {
        setCargandoPred(false)
      }
    }, 350) // debounce
    return () => { clearTimeout(t); ctrl.abort() }
  }, [pub.titulo]) // eslint-disable-line react-hooks/exhaustive-deps

  // Agrupar la lista completa por grupo para el fallback "Ver todas"
  const gruposCompletos = CATEGORIAS_LIMPIEZA.reduce((acc, c) => {
    (acc[c.grupo] = acc[c.grupo] || []).push(c)
    return acc
  }, {})

  const idsSugeridos = new Set(sugerencias.map(s => s.id))

  return (
    <>
      <h2>2 · Categoría ML</h2>
      <div className="wz-step-desc">
        ML organiza el catálogo en categorías. Usamos el predictor oficial de MercadoLibre
        (<code>domain_discovery/search</code>) para sugerirte las mejores según el título del producto.
        Podés buscar libre abajo si no coincide.
      </div>

      {/* Buscador libre — 15-jul-2026 */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="🔍 Buscar categoría por nombre o pegar un ID (ej: MLU436132)"
          style={{ width: '100%', padding: '10px 14px', border: '1px solid #E6E4DE', borderRadius: 8, fontSize: 14, background: '#fff' }}
        />
        {busqueda.trim().length >= 2 && (
          <div style={{ marginTop: 8, background: '#F8F6F1', border: '1px solid #E6E4DE', borderRadius: 8, padding: 10 }}>
            {cargandoBusquedaAPI && <div style={{ fontSize: 12, color: '#888780', padding: 4 }}>Buscando en MercadoLibre…</div>}

            {/* MLU-id pegado directo */}
            {catManualInfo && (
              <label className={`wz-option ${pub.categoria_id === catManualInfo.id ? 'selected' : ''}`} style={{ borderLeftColor: '#C5A059', borderLeftWidth: 3, borderLeftStyle: 'solid', margin: 4 }}>
                <input type="radio" name="cat" value={catManualInfo.id} checked={pub.categoria_id === catManualInfo.id} onChange={() => update('categoria_id', catManualInfo.id)} disabled={catManualInfo.listing_allowed === false} />
                <div>
                  <div className="wz-option-title">
                    {catManualInfo.name}
                    <span style={{ marginLeft: 8, padding: '2px 8px', background: '#C5A059', color: 'white', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>ID DIRECTO</span>
                    {catManualInfo.listing_allowed === false && <span style={{ marginLeft: 8, color: '#B91C1C', fontSize: 11 }}>⚠ padre — no se puede publicar</span>}
                  </div>
                  <div className="wz-option-meta">{catManualInfo.id}</div>
                </div>
              </label>
            )}

            {/* Resultados del catálogo interno Limpack */}
            {catalogoFiltrado.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888780', textTransform: 'uppercase', letterSpacing: 0.5, margin: '6px 8px 4px' }}>
                  del catálogo Limpack ({catalogoFiltrado.length})
                </div>
                {catalogoFiltrado.map(c => (
                  <label key={'cf-' + c.id} className={`wz-option ${pub.categoria_id === c.id ? 'selected' : ''}`} style={{ margin: 4 }}>
                    <input type="radio" name="cat" value={c.id} checked={pub.categoria_id === c.id} onChange={() => update('categoria_id', c.id)} />
                    <div>
                      <div className="wz-option-title">{c.label}</div>
                      <div className="wz-option-meta">{c.id} · {c.grupo}</div>
                    </div>
                  </label>
                ))}
              </>
            )}

            {/* Resultados de la búsqueda libre en ML */}
            {busquedaAPI.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888780', textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 8px 4px' }}>
                  ml sugiere ({busquedaAPI.length})
                </div>
                {busquedaAPI.map(c => (
                  <label key={'ba-' + c.id} className={`wz-option ${pub.categoria_id === c.id ? 'selected' : ''}`} style={{ margin: 4 }}>
                    <input type="radio" name="cat" value={c.id} checked={pub.categoria_id === c.id} onChange={() => update('categoria_id', c.id)} />
                    <div>
                      <div className="wz-option-title">{c.label}</div>
                      <div className="wz-option-meta">{c.id}{c.domain_name ? ` · dominio ${c.domain_name}` : ''}</div>
                    </div>
                  </label>
                ))}
              </>
            )}

            {!cargandoBusquedaAPI && !catManualInfo && catalogoFiltrado.length === 0 && busquedaAPI.length === 0 && (
              <div style={{ fontSize: 12, color: '#888780', padding: 8 }}>Sin resultados. Probá con otro término o pegá el ID de la categoría (ej: <code>MLU436132</code>).</div>
            )}
          </div>
        )}
      </div>

      {cargandoPred && (
        <div style={{ padding: '12px 16px', background: '#F8F6F1', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#888780' }}>
          Consultando el predictor de MercadoLibre…
        </div>
      )}

      {errorPred && (
        <div style={{ padding: '12px 16px', background: '#FEF3E7', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#8A5C00' }}>
          No pude consultar el predictor de ML ({errorPred}). Podés elegir manual abajo.
        </div>
      )}

      {sugerencias.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1A3461', textTransform: 'uppercase', letterSpacing: 0.5, margin: '4px 0 8px' }}>
            🎯 Sugeridas por MercadoLibre según el título
          </div>
          {sugerencias.map(c => (
            <label key={c.id} className={`wz-option ${pub.categoria_id === c.id ? 'selected' : ''}`} style={{ borderLeftColor: '#00A8B5', borderLeftWidth: 3, borderLeftStyle: 'solid' }}>
              <input type="radio" name="cat" value={c.id} checked={pub.categoria_id === c.id} onChange={() => update('categoria_id', c.id)} />
              <div>
                <div className="wz-option-title">
                  {c.label}
                  {c === sugerencias[0] && <span style={{ marginLeft: 8, padding: '2px 8px', background: '#00A8B5', color: 'white', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>MEJOR MATCH</span>}
                </div>
                <div className="wz-option-meta">
                  {c.id}{c.domain_name ? ` · dominio ${c.domain_name}` : ''}
                </div>
              </div>
            </label>
          ))}
        </>
      )}

      <div style={{ margin: '16px 0 8px' }}>
        <button
          type="button"
          onClick={() => setVerTodas(v => !v)}
          style={{ background: 'none', border: '1px solid #E6E4DE', color: '#1A3461', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          {verTodas ? '↑ Ocultar catálogo completo' : `↓ Ver catálogo completo (${CATEGORIAS_LIMPIEZA.length} categorías)`}
        </button>
      </div>

      {verTodas && Object.entries(gruposCompletos).map(([grupo, cats]) => (
        <div key={grupo} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#888780', textTransform: 'uppercase', letterSpacing: 0.5, margin: '8px 0 4px', padding: '0 8px' }}>
            {grupo}
          </div>
          {cats.map(c => (
            <label key={c.id} className={`wz-option ${pub.categoria_id === c.id ? 'selected' : ''}`}>
              <input type="radio" name="cat" value={c.id} checked={pub.categoria_id === c.id} onChange={() => update('categoria_id', c.id)} />
              <div>
                <div className="wz-option-title">
                  {c.label}
                  {idsSugeridos.has(c.id) && <span style={{ marginLeft: 8, color: '#00A8B5', fontSize: 11 }}>🎯 sugerida</span>}
                </div>
                <div className="wz-option-meta">{c.id}</div>
              </div>
            </label>
          ))}
        </div>
      ))}
    </>
  )
}

// Auto-detección de atributos ML — v2 (2026-07-14).
// PROBLEMA v1: inventaba IDs (PRODUCT_NAME, VOLUME, VOLUME_UNIT, WASHES_UNIT, HYPOALLERGENIC,
// BIODEGRADABLE, UNSCENTED, PET_SAFE, CONCENTRATED) que NO existen en las categorías reales
// de ML. ML aceptaba el POST (201) pero DESCARTABA en silencio esos atributos → la ficha
// quedaba sin "Características principales" (caso MLU696771111, ver ml_logs 2026-07-14).
// v2: la detección devuelve CONCEPTOS agnósticos de categoría; el mapeo a los IDs REALES que
// ML acepta se hace en Step3 contra /categories/{id}/attributes (mapearConceptosAAttrs).

// Semilla mínima para la hidratación inicial: solo BRAND (id real y obligatorio en estas
// categorías). El resto lo completa Step3 cuando ya bajó los atributos reales de la categoría.
function autoAtributos(prod) {
  return prod?.marca ? { BRAND: prod.marca } : {}
}

// Detecta conceptos del producto SIN atarlos a un id de ML (columnas del producto + regex
// sobre nombre/presentación como fallback). El mapeo a ids reales lo hace mapearConceptosAAttrs.
function conceptosProducto(prod) {
  const texto = `${prod?.nombre || ''} ${prod?.presentacion || ''}`.toLowerCase()
  const c = {}
  if (prod?.marca)  c.brand  = prod.marca
  if (prod?.nombre) c.nombre = prod.nombre
  // Volumen: "2L" / "2 L" / "500ml" / "1.5 lt"
  const mVol = texto.match(/(\d+(?:[.,]\d+)?)\s*(l|ml|lt|litros?|cc)\b/i)
  if (mVol) {
    const num = parseFloat(mVol[1].replace(',', '.'))
    const u = mVol[2].toLowerCase()
    c.volume = { num, unit: (u.startsWith('ml') || u === 'cc') ? 'mL' : 'L' }
  }
  // Peso NETO del contenido (NO peso_g, que es peso del paquete p/envío): "5kg" / "500 g"
  const mPeso = texto.match(/(\d+(?:[.,]\d+)?)\s*(kg|kilos?|gr|gramos?|g)\b/i)
  if (mPeso) {
    const num = parseFloat(mPeso[1].replace(',', '.'))
    const u = mPeso[2].toLowerCase()
    c.weight = { num, unit: u.startsWith('k') ? 'kg' : 'g' }
  }
  // Formato
  if      (/l[ií]quid/i.test(texto))                        c.format = 'Líquido'
  else if (/polvo/i.test(texto))                            c.format = 'Polvo'
  else if (/\bgel\b/i.test(texto))                          c.format = 'Gel'
  else if (/c[aá]psulas?|\bpods?\b|pastillas?/i.test(texto)) c.format = 'Cápsulas'
  // Booleans: columna del producto > regex del nombre como fallback
  const yesNo = (v, re) => v === true ? true : v === false ? false : (re.test(texto) ? true : null)
  const hipo = yesNo(prod?.hipoalergenico, /hipoalerg|piel\s+sensible/i)
  if (hipo !== null) c.hypoallergenic = hipo
  const bio = yesNo(prod?.biodegradable, /biodegrad|sustentab|eco[\s-]?friendly/i)
  if (bio !== null) c.biodegradable = bio
  const pet = yesNo(prod?.apto_ninos_mascotas, /mascota|niñ|bebé|baby|infantil/i)
  if (pet !== null) c.petSafe = pet
  // Fragancia — la columna `aroma` es la fuente principal (casi siempre cargada). Los sin-perfume
  // dicen "sin aroma"/"neutro". sin_fragancia (bool) e hipoalergénico refuerzan "Sin fragancia".
  const aromaRaw = String(prod?.aroma || '').trim()
  const esSinAroma = prod?.sin_fragancia === true
    || /sin\s*aroma|sin\s*fragancia|sin\s*perfume|neutro/i.test(aromaRaw)
    || /sin\s+fragancia|sin\s+perfume|hipoalerg/i.test(texto)
  if (esSinAroma) c.unscented = true
  else if (aromaRaw) c.fragrance = aromaRaw

  // Tipo de envase — desde nombre + presentación por keywords (presentacion suele traer el tamaño,
  // pero nombre/presentacion a veces dicen el envase: "Frasco con Válvula", "Doypack", "Home Spray").
  const envaseTxt = `${prod?.nombre || ''} ${prod?.presentacion || ''}`.toLowerCase()
  const ENVASES = [
    [/bid[oó]n/, 'Bidón'],
    [/doypack|pouch|sachet/, 'Doypack'],
    [/recarga|repuesto|refill/, 'Recarga'],
    [/home\s*spray|gatillo|trigger|\bspray\b/, 'Spray'],
    [/aerosol/, 'Aerosol'],
    [/frasco/, 'Frasco'],
    [/roll[\s-]?on/, 'Roll-on'],
    [/balde/, 'Balde'],
    [/botella/, 'Botella'],
  ]
  for (const [re, val] of ENVASES) { if (re.test(envaseTxt)) { c.packaging = val; break } }
  return c
}

// Definición de cada concepto: ids candidatos de ML (match exacto, gana el primero que exista)
// + palabras a buscar en el .name del atributo real (match semántico, category-agnostic).
// vt = value_type esperado para desambiguar el match por nombre.
const MAPEO_CONCEPTOS = {
  brand:          { ids: ['BRAND'],                                             nombre: [] },
  nombre:         { ids: ['MANUAL_TITLE'],                                      nombre: [] },
  volume:         { ids: ['NET_VOLUME', 'VOLUME', 'CONTENT_VOLUME'],            nombre: ['volumen', 'capacid', 'contenido', 'volume'], vt: 'number_unit' },
  weight:         { ids: ['NET_WEIGHT', 'WEIGHT', 'CONTENT_WEIGHT'],            nombre: ['peso', 'weight'],                            vt: 'number_unit' },
  format:         { ids: ['DETERGENT_FORMAT', 'FORMAT', 'PRESENTATION'],        nombre: ['formato', 'presentaci', 'tipo de producto', 'type'] },
  packaging:      { ids: ['PACKAGING_TYPE', 'PACKAGE_TYPE', 'CONTAINER_TYPE'],  nombre: ['envase', 'empaque', 'packaging', 'tipo de envase', 'recipiente'] },
  hypoallergenic: { ids: ['IS_SUITABLE_FOR_SENSITIVE_SKIN', 'IS_HYPOALLERGENIC'], nombre: ['sensible', 'alérgic', 'alergic', 'hipoalerg', 'sensitive'], vt: 'boolean' },
  biodegradable:  { ids: ['IS_BIODEGRADABLE', 'WITH_POSITIVE_IMPACT'],          nombre: ['biodegrad', 'impacto positivo', 'sustentab', 'eco'], vt: 'boolean' },
  petSafe:        { ids: ['IS_SAFE_FOR_PETS', 'SUITABLE_FOR_PETS'],             nombre: ['mascota', 'pets', 'niño', 'nino', 'children', 'infantil'], vt: 'boolean' },
  unscented:      { ids: ['FRAGRANCE'],                                         nombre: ['fragranc', 'aroma', 'olor', 'perfum'] },
  fragrance:      { ids: ['FRAGRANCE'],                                         nombre: ['fragranc', 'aroma', 'olor', 'perfum'] },
}

// Atributos de CANTIDAD que nunca deben recibir un valor de texto. El match semántico por nombre
// los agarraba por casualidad y ML rechazaba la publicación.
// Caso real (27-jul, JP01 Aromatizante): el concepto `packaging` busca "envase" para encontrar el
// TIPO de envase (Frasco, Aerosol…), y matcheaba con "Unidades por envase" (UNITS_PER_PACKAGE),
// que es numérico → ML devolvía "El valor que ingresaste en Unidades por envase es incorrecto".
const ATTR_IDS_CANTIDAD = ['UNITS_PER_PACKAGE', 'UNITS_PER_PACK', 'PACKAGE_UNITS', 'ITEMS_PER_PACK']

// Resuelve un concepto al atributo REAL de la categoría: 1) por id exacto, 2) por nombre semántico.
function resolverAttrDeCategoria(catAttrs, def) {
  // Un atributo es editable si ML no lo marcó como read_only/hidden/fixed.
  // Antes el match por ID NO validaba esto (solo el match por nombre) y se mandaba MANUAL_TITLE,
  // que en varias categorías es read_only → ML respondía "Attribute [MANUAL_TITLE] ignored
  // because it is not modifiable" (27-jul, JP01).
  const editable = (a) => {
    const t = a?.tags || {}
    return !(t.hidden || t.read_only || t.fixed)
  }
  const porId = new Map((catAttrs || []).map(a => [a.id, a]))
  for (const id of def.ids || []) {
    const a = porId.get(id)
    if (a && editable(a)) return a
  }
  for (const sub of def.nombre || []) {
    const hit = (catAttrs || []).find(a => {
      const t = a.tags || {}
      if (t.hidden || t.read_only || t.fixed) return false
      if (def.vt && a.value_type !== def.vt) return false
      // Nunca resolver a un atributo de cantidad por match de nombre
      if (ATTR_IDS_CANTIDAD.includes(a.id)) return false
      // Los conceptos SIN value_type declarado (packaging, format, fragrance…) son textuales:
      // no pueden caer en un atributo numérico aunque el nombre coincida.
      if (!def.vt && (a.value_type === 'number' || a.value_type === 'number_unit')) return false
      return (a.name || '').toLowerCase().includes(sub)
    })
    if (hit) return hit
  }
  return null
}

// Formatea el valor de un concepto al value_type real del atributo ML (value_name; '' si no matchea).
function valorConceptoParaAttr(attr, concepto, val) {
  const vt = attr.value_type
  if (vt === 'boolean') return val ? 'Sí' : 'No'
  if (vt === 'number_unit') {
    if (val && typeof val === 'object') {
      // respetar allowed_units si existen (ej. 'L' vs 'mL')
      const permitidas = (attr.allowed_units || []).map(u => u.id)
      const unit = permitidas.length && !permitidas.includes(val.unit)
        ? (permitidas.find(u => u.toLowerCase() === val.unit.toLowerCase()) || permitidas[0])
        : val.unit
      return `${val.num} ${unit}`.trim()
    }
    return String(val ?? '')
  }
  if (vt === 'list') {
    const buscado = concepto === 'unscented' ? 'sin fragancia'
                  : (val && typeof val === 'object') ? '' : String(val ?? '').toLowerCase()
    if (!buscado) return ''
    const vals = attr.values || []
    const hit = vals.find(v => v.name.toLowerCase() === buscado)
             || vals.find(v => v.name.toLowerCase().includes(buscado))
    return hit ? hit.name : ''
  }
  // string (algunos strings traen values sugeridos: PACKAGING_TYPE, FRAGRANCE, LINE…).
  if (concepto === 'unscented') return 'Sin fragancia'
  if (val && typeof val === 'object') return ''
  const txt = String(val ?? '').trim()
  const vals = attr.values || []
  if (txt && vals.length) {
    const lc = txt.toLowerCase()
    const hit = vals.find(v => (v.name || '').toLowerCase() === lc)
             || vals.find(v => (v.name || '').toLowerCase().includes(lc) || lc.includes((v.name || '').toLowerCase()))
    if (hit) return hit.name  // usa el value_name exacto de ML (mejor para el score)
  }
  return txt  // string libre (ML acepta free-text en estos atributos)
}

// Dados los atributos REALES de la categoría + los conceptos del producto, devuelve:
//   { mapped: { idRealDeML: value_name }, sinMapear: [ { concepto, valor } ] }
// mapped = solo IDs que ML acepta. sinMapear = datos del producto que NO encontraron atributo
// (para el chip amarillo en el wizard — nunca se silencian).
function mapearConceptosAAttrs(catAttrs, conceptos) {
  const mapped = {}
  const sinMapear = []
  for (const [concepto, val] of Object.entries(conceptos)) {
    if (concepto === 'unscented' && !val) continue           // "sin fragancia" solo si es true
    if ((concepto === 'hypoallergenic' || concepto === 'biodegradable' || concepto === 'petSafe') && val === false) continue
    const def = MAPEO_CONCEPTOS[concepto]
    if (!def) continue
    const attr = resolverAttrDeCategoria(catAttrs, def)
    const value = attr ? valorConceptoParaAttr(attr, concepto, val) : ''
    if (attr && value) mapped[attr.id] = value
    else if (concepto !== 'nombre') sinMapear.push({ concepto, valor: val })
  }
  return { mapped, sinMapear }
}

// Etiquetas legibles de cada concepto para el chip amarillo "no se mapeó".
const LABEL_CONCEPTO = {
  brand: 'Marca', nombre: 'Nombre', volume: 'Volumen', weight: 'Peso',
  format: 'Formato', hypoallergenic: 'Hipoalergénico', biodegradable: 'Biodegradable',
  petSafe: 'Apto niños/mascotas', unscented: 'Sin fragancia', fragrance: 'Aroma',
  packaging: 'Tipo de envase',
}

// Atributos que casi nunca aplican a productos de limpieza: si el matcher no encuentra dato y el
// atributo quedó vacío, autocompletamos "No aplica" (vacío penaliza el score de ML; "No aplica"
// cuenta como completo). Curado a propósito — NO autollenar cualquier atributo con "No aplica".
const NO_APLICA_SI_VACIO = ['MODEL', 'MPN']
function textoConcepto(valor) {
  if (valor && typeof valor === 'object') return `${valor.num} ${valor.unit}`
  if (valor === true) return 'Sí'; if (valor === false) return 'No'
  return String(valor ?? '')
}

// Step3 dinámico: carga los atributos REALES de la categoría desde ML (endpoint público) y
// arma un input por cada uno según su value_type. Auto-detecta atributos desde las columnas del
// producto y los mapea a los ids REALES que ML acepta (mapearConceptosAAttrs), limpia ids
// inventados heredados y avisa con chip amarillo lo que no pudo mapear.
// Atributos distintivos que NO se heredan del sibling — son los que diferencian variantes
// (ej: fragancia distinta en cada suavizante de la familia).
const ATTRS_NO_HEREDABLES = new Set([
  'FRAGRANCE',    // Fragancia
  'AROMA',        // Aroma
  'SCENT',        // Scent
  'FLAVOR',       // Sabor (por si aplica a jabones)
  'COLOR',        // Color (por si aplica a variantes color)
  'MANUAL_TITLE', // Título propio de cada ítem
])

function Step3({ pub, updateAttr, prod }) {
  const attrs = pub.attributes || {}
  const [catAttrs, setCatAttrs] = useState([])
  const [cargando, setCargando] = useState(false)
  const [err, setErr] = useState(null)
  const [sinMapear, setSinMapear] = useState([])
  const [idsInvalidos, setIdsInvalidos] = useState([])
  // Herencia de familia: si hay un ítem activo con misma marca+categoría+presentación,
  // heredamos sus atributos (excepto los distintivos como FRAGRANCE).
  const [siblingInfo, setSiblingInfo] = useState(null) // { sku, nombre, ml_item_id, attributes }
  const [heredadosIds, setHeredadosIds] = useState(new Set())

  useEffect(() => {
    if (!pub.categoria_id) { setCatAttrs([]); return }
    const ctrl = new AbortController()
    setCargando(true); setErr(null)
    fetchCategoriaAttrs(pub.categoria_id, ctrl.signal)
      .then(data => { if (Array.isArray(data)) setCatAttrs(data) })
      .catch(e => { if (e.name !== 'AbortError') setErr(e.message) })
      .finally(() => setCargando(false))
    return () => ctrl.abort()
  }, [pub.categoria_id])

  // Detectar sibling activo de la misma familia (marca + categoría + presentación)
  useEffect(() => {
    if (!prod?.marca || !pub.categoria_id || !prod?.presentacion) { setSiblingInfo(null); return }
    let cancel = false
    ;(async () => {
      try {
        // Buscar OTRA publicación activa del seller con misma marca+categoría+presentación
        // (excluyendo el producto actual — para no auto-heredarse a sí mismo)
        const { data, error } = await supabase
          .from('ml_publicaciones')
          .select('ml_item_id, attributes, producto_id, productos!inner(sku, nombre, marca, presentacion)')
          .eq('status', 'active')
          .eq('categoria_id', pub.categoria_id)
          .eq('productos.marca', prod.marca)
          .eq('productos.presentacion', prod.presentacion)
          .neq('producto_id', prod.id)
          .limit(1)
          .maybeSingle()
        if (cancel) return
        if (error || !data) { setSiblingInfo(null); return }
        setSiblingInfo({
          sku: data.productos?.sku,
          nombre: data.productos?.nombre,
          ml_item_id: data.ml_item_id,
          attributes: data.attributes || {},
        })
      } catch (e) {
        if (!cancel) setSiblingInfo(null)
      }
    })()
    return () => { cancel = true }
  }, [prod?.marca, prod?.presentacion, prod?.id, pub.categoria_id])

  // Al llegar los atributos reales: 1) limpiar ids que ML NO reconoce (inventados v1 / drafts
  // viejos), 2) auto-precargar los conceptos del producto en los ids reales sin pisar lo que el
  // user ya cargó, 3) registrar lo que no se pudo mapear para el chip amarillo.
  useEffect(() => {
    if (!catAttrs.length) return
    const validIds = new Set(catAttrs.map(a => a.id))
    const actuales = pub.attributes || {}
    // 1) limpiar ids inválidos (los descartaría ML igual → mejor sacarlos antes)
    const invalidos = Object.keys(actuales).filter(k => actuales[k] && !validIds.has(k))
    setIdsInvalidos(invalidos)
    invalidos.forEach(k => updateAttr(k, ''))
    // 2) auto-mapear conceptos del producto → ids reales
    if (prod) {
      const { mapped, sinMapear: sm } = mapearConceptosAAttrs(catAttrs, conceptosProducto(prod))
      Object.entries(mapped).forEach(([id, value]) => { if (!actuales[id]) updateAttr(id, value) })
      setSinMapear(sm)
    }
    // 3) "No aplica" para atributos que no aplican a limpieza (MODEL, MPN…) y quedaron vacíos.
    for (const id of NO_APLICA_SI_VACIO) {
      const a = catAttrs.find(x => x.id === id)
      const t = a?.tags || {}
      if (a && a.value_type === 'string' && !t.hidden && !t.read_only && !t.fixed && !actuales[id]) updateAttr(id, 'No aplica')
    }
    // 4) HERENCIA DE FAMILIA: si hay sibling activo, copiar sus atributos como default
    //    (solo los que NO están ya seteados por el user y NO son distintivos como FRAGRANCE).
    if (siblingInfo?.attributes) {
      const heredados = new Set()
      const actualesAhora = pub.attributes || {}
      Object.entries(siblingInfo.attributes).forEach(([id, value]) => {
        if (!validIds.has(id)) return                    // atributo no válido en esta categoría
        if (ATTRS_NO_HEREDABLES.has(id)) return           // distintivo (fragancia, color…)
        if (!value || String(value).trim() === '') return // vacío en el sibling
        if (actualesAhora[id] && String(actualesAhora[id]).trim() !== '') return // ya cargado
        updateAttr(id, value)
        heredados.add(id)
      })
      setHeredadosIds(heredados)
    } else {
      setHeredadosIds(new Set())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catAttrs, prod, siblingInfo])

  const isReq = (a) => { const t = a.tags || {}; return !!(t.required || t.catalog_required || t.conditional_required) }
  const editables = catAttrs.filter(a => {
    const t = a.tags || {}
    return !t.hidden && !t.read_only && !t.fixed
  })
  const ordenados = [...editables].sort((a, b) => (isReq(b) ? 1 : 0) - (isReq(a) ? 1 : 0))
  const reqCount = editables.filter(isReq).length
  const requiredVacios = editables.filter(isReq).filter(a => !String(attrs[a.id] ?? '').trim())

  return (
    <>
      <h2>3 · Atributos (ficha técnica)</h2>
      <div className="wz-step-desc">Estos son los atributos <strong>reales de la categoría elegida</strong> (paso 2). Los marcados con <strong>*</strong> son obligatorios de MELI. Cuantos más completes, mejor rankea y más completa queda la ficha del producto.</div>

      {cargando && <div className="wz-info">Cargando atributos de la categoría desde ML…</div>}
      {err && <div className="wz-info" style={{ background: '#FDEEEE', color: '#791F1F' }}>No se pudieron cargar los atributos de ML ({err}). Revisá la categoría en el paso 2.</div>}
      {!cargando && !err && !pub.categoria_id && <div className="wz-info">Elegí primero la categoría en el paso 2.</div>}
      {!cargando && !err && pub.categoria_id && editables.length === 0 && <div className="wz-info">Esta categoría no expone atributos editables.</div>}

      {idsInvalidos.length > 0 && (
        <div style={{ padding: '9px 14px', background: '#FDEEEE', borderRadius: 8, margin: '10px 0', fontSize: 11.5, color: '#791F1F', fontWeight: 600 }}>
          Se quitaron {idsInvalidos.length} atributo(s) que ML no reconoce en esta categoría (ML los descartaba en silencio): {idsInvalidos.join(', ')}
        </div>
      )}

      {reqCount > 0 && (
        <div style={{ padding: '9px 14px', background: '#FEF3C7', borderRadius: 8, margin: '10px 0', fontSize: 11.5, color: '#78350F', fontWeight: 600 }}>
          {reqCount} atributo(s) obligatorio(s) de MELI — marcados con *
        </div>
      )}

      {requiredVacios.length > 0 && (
        <div style={{ padding: '9px 14px', background: '#FDEEEE', borderRadius: 8, margin: '10px 0', fontSize: 11.5, color: '#791F1F', fontWeight: 600 }}>
          Faltan {requiredVacios.length} obligatorio(s) sin completar: {requiredVacios.map(a => a.name).join(', ')}. Completalos para mejorar el score de la publicación.
        </div>
      )}

      {sinMapear.length > 0 && (
        <div style={{ margin: '10px 0', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11.5, color: '#92400E', fontWeight: 600 }}>Datos del producto que no encontraron atributo en esta categoría (buscalos manual abajo o dejalos así):</span>
          {sinMapear.map(({ concepto, valor }) => (
            <span key={concepto} style={{ padding: '3px 9px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 999, fontSize: 11, color: '#78350F', fontWeight: 600 }}>
              {LABEL_CONCEPTO[concepto] || concepto}: {textoConcepto(valor)}
            </span>
          ))}
        </div>
      )}

      {siblingInfo && heredadosIds.size > 0 && (
        <div style={{
          padding: '10px 14px', background: 'rgba(0,168,181,0.10)', border: '1px solid rgba(0,168,181,0.35)',
          borderRadius: 8, margin: '10px 0', fontSize: 12, color: '#00888F', lineHeight: 1.55
        }}>
          <strong>👨‍👩‍👧 Detectamos familia:</strong> este producto comparte marca, categoría y presentación con <strong>{siblingInfo.nombre}</strong> ({siblingInfo.sku} · <code>{siblingInfo.ml_item_id}</code>). Heredamos <strong>{heredadosIds.size} atributo(s)</strong> automáticamente (los marcados con chip azul). La fragancia/aroma no se hereda — es lo que diferencia esta variante.
        </div>
      )}

      {ordenados.map(a => (
        <AttrInput
          key={a.id}
          attr={a}
          value={attrs[a.id] ?? ''}
          required={isReq(a)}
          heredado={heredadosIds.has(a.id)}
          onChange={(v) => { updateAttr(a.id, v); if (heredadosIds.has(a.id)) { const ns = new Set(heredadosIds); ns.delete(a.id); setHeredadosIds(ns) } }}
        />
      ))}
    </>
  )
}

// Chip que indica que el valor vino heredado del sibling de la familia (mismo marca+cat+pres)
function ChipHeredado() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.02em',
      padding: '2px 7px', borderRadius: 999,
      background: 'rgba(0,168,181,0.15)', color: '#00888F',
      border: '1px solid rgba(0,168,181,0.30)',
      marginLeft: 6, verticalAlign: 'middle',
    }} title="Este valor viene heredado del ítem hermano de la familia. Al editar, deja de estar heredado.">
      👨‍👩‍👧 DE LA FAMILIA
    </span>
  )
}

// Valida el dígito verificador de un GTIN/EAN/UPC (mod-10, largos 8/12/13/14). Evita subir un
// código inventado a mano, que ML modera. Si no tiene código, mejor dejarlo vacío.
function gtinValido(raw) {
  const s = String(raw || '').replace(/\D/g, '')
  if (![8, 12, 13, 14].includes(s.length)) return false
  const d = s.split('').map(Number)
  const check = d.pop()
  let sum = 0
  for (let i = d.length - 1, mult = 3; i >= 0; i--, mult = mult === 3 ? 1 : 3) sum += d[i] * mult
  const calc = (10 - (sum % 10)) % 10
  return calc === check
}

// Renderiza un input de atributo MELI según su value_type (boolean, list, number_unit, texto).
function AttrInput({ attr, value, required, onChange, heredado }) {
  const label = <>{attr.name}{required ? ' *' : ''}{heredado ? <ChipHeredado /> : null}</>
  const vt = attr.value_type
  const values = attr.values || []
  const esBooleano = vt === 'boolean' || (values.length > 0 && values.length <= 3 && values.every(v => ['Sí', 'Si', 'No'].includes(v.name)))

  if (esBooleano) {
    return (
      <div className="wz-form-row" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <label className="wz-lbl" style={{ flex: 1, marginBottom: 0 }}>{label}</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {['Sí', 'No', ''].map(opt => (
            <button key={opt || 'skip'} type="button" onClick={() => onChange(opt)}
              style={{
                padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                border: '1px solid ' + (value === opt ? '#00A8B5' : '#CBD5E1'),
                background: value === opt ? '#00A8B5' : '#fff',
                color: value === opt ? '#fff' : '#475569',
              }}
            >{opt || 'omitir'}</button>
          ))}
        </div>
      </div>
    )
  }

  if (vt === 'list' && values.length > 0) {
    return (
      <div className="wz-form-row">
        <label className="wz-lbl">{label}</label>
        <select className="wz-input" value={value} onChange={e => onChange(e.target.value)}>
          <option value="">—</option>
          {values.map(v => <option key={v.id || v.name} value={v.name}>{v.name}</option>)}
        </select>
      </div>
    )
  }

  if (vt === 'number_unit') {
    const units = attr.allowed_units || []
    const m = String(value).match(/^\s*([\d.,]+)\s*(.*)$/)
    const num = m ? m[1] : ''
    const unit = (m && m[2]) ? m[2] : (attr.default_unit || units[0]?.id || '')
    return (
      <div className="wz-form-row" style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 2 }}>
          <label className="wz-lbl">{label}</label>
          <input type="text" className="wz-input" value={num} onChange={e => onChange(e.target.value ? `${e.target.value} ${unit}`.trim() : '')} placeholder="ej. 2" />
        </div>
        <div style={{ flex: 1 }}>
          <label className="wz-lbl">Unidad</label>
          <select className="wz-input" value={unit} onChange={e => onChange(num ? `${num} ${e.target.value}`.trim() : '')}>
            {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
    )
  }

  const esGtin = /GTIN|^EAN|^UPC/i.test(attr.id)
  const gtinOk = esGtin && String(value).trim() ? gtinValido(value) : null
  return (
    <div className="wz-form-row">
      <label className="wz-lbl">{label}</label>
      <input type="text" className="wz-input" value={value} maxLength={attr.value_max_length || undefined}
        onChange={e => onChange(e.target.value)} placeholder={esGtin ? 'Código de barras EAN/UPC — dejalo vacío si no tiene' : attr.name} />
      {esGtin && (
        <div className={`wz-help ${gtinOk === false ? 'warn' : gtinOk ? 'ok' : ''}`}>
          {gtinOk === true && '✓ Código válido.'}
          {gtinOk === false && '⚠ El dígito verificador no cierra. Revisalo; si el producto no tiene código de barras, dejalo vacío (mejor vacío que inventado — ML modera los GTIN falsos).'}
          {gtinOk === null && 'Si el producto no tiene código de barras, dejalo vacío. No lo inventes: ML modera los GTIN falsos.'}
        </div>
      )}
    </div>
  )
}

function Step4({ pub, update, producto, familiaHermano }) {
  const len = (pub.titulo || '').length
  const lenFam = (pub.family_name || '').length
  const heredado = !!familiaHermano?.family_name && pub.family_name === familiaHermano.family_name
  const sugerencia = derivarFamilyName(pub.titulo, producto?.marca)
  // ML penaliza/rechaza títulos con palabras promocionales o datos de contacto. Aviso, no bloqueo.
  const avisoTitulo = (pub.family_name || '').match(/\b(env[ií]o gratis|gratis|oferta|promoci[oó]n|promo|liquidaci[oó]n|descuento|rebaja|2x1|3x2|imperdible|whatsapp|whats|http|www\.|\.com|\.uy)\b|[%@]/i)
  return (
    <>
      <h2>4 · Título de la publicación</h2>
      <div className="wz-step-desc">
        Esta cuenta publica con <strong>Publicaciones de Usuario</strong> de ML: el título que ve el comprador
        NO es el campo "Título", es el <strong>Nombre de familia</strong>. ML copia el nombre de familia tal cual.
      </div>

      <div className="wz-form-row">
        <label className="wz-lbl">Nombre de familia — esto es lo que ve el comprador</label>
        <input
          type="text" className="wz-input" value={pub.family_name || ''}
          onChange={e => update('family_name', e.target.value)} maxLength={60}
          placeholder="ej. Agua Sanitaria Lavandina Brilha Sul 5 L"
        />
        <div className={`wz-help ${lenFam > 55 ? 'warn' : lenFam > 0 ? 'ok' : 'warn'}`}>
          {lenFam}/60 caracteres{lenFam === 0 && ' · obligatorio para publicar'}
        </div>
        {avisoTitulo && (
          <div className="wz-help warn">
            ⚠ Contiene “{avisoTitulo[0]}”: ML no permite palabras promocionales ni datos de contacto en el título. Sacalo para no arriesgar moderación.
          </div>
        )}
        {sugerencia && pub.family_name !== sugerencia && (
          <button type="button" className="wz-btn-ghost" style={{ marginTop: 6, fontSize: 12 }}
            onClick={() => update('family_name', sugerencia)}>
            ↻ Sugerir desde el título: “{sugerencia}”
          </button>
        )}
      </div>

      {heredado && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12.5, color: '#78350F', lineHeight: 1.6 }}>
          <strong>⚠ Este producto va a compartir familia</strong> con {familiaHermano.ml_item_id ? <>“{familiaHermano.titulo?.trim()}” ({familiaHermano.ml_item_id})</> : `“${familiaHermano.titulo?.trim()}”`}:
          <div style={{ margin: '6px 0', fontWeight: 700 }}>“{familiaHermano.family_name}”</div>
          ML los agrupa como variantes del mismo producto y <strong>los dos van a mostrar ese mismo título</strong>, distinguiéndose por sus atributos (aroma, tamaño).
          Si querés que sea una publicación aparte, editá el nombre de familia arriba.
        </div>
      )}
      {!heredado && familiaHermano?.family_name && (
        <div style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12.5, color: '#334155', lineHeight: 1.6 }}>
          Hay otra publicación activa de {producto?.marca} en esta categoría (“{familiaHermano.titulo?.trim()}”) con familia <strong>“{familiaHermano.family_name}”</strong>. Con el nombre actual va a ser una publicación <strong>independiente</strong>.
          <button type="button" className="wz-btn-ghost" style={{ marginTop: 6, fontSize: 12, display: 'block' }}
            onClick={() => update('family_name', familiaHermano.family_name)}>
            Compartir familia con esa publicación
          </button>
        </div>
      )}

      <div className="wz-form-row">
        <label className="wz-lbl">Título interno (referencia Limpack — ML no lo publica)</label>
        <input type="text" className="wz-input" value={pub.titulo} onChange={e => update('titulo', e.target.value)} maxLength={60} placeholder="ej. Detergente Vajilla NEBA Limón 1L Lavavajilla" />
        <div className={`wz-help ${len > 55 ? 'warn' : len > 0 ? 'ok' : ''}`}>
          {len}/60 caracteres {len > 60 && '· ML va a rechazarlo'}
        </div>
      </div>
    </>
  )
}

function Step5({ pub, update, config, productoId, setMsg }) {
  const len = (pub.descripcion || '').length
  const [generando, setGenerando] = useState(false)
  async function sugerirIA() {
    setGenerando(true)
    try {
      const { data, error } = await supabase.functions.invoke('ml-sugerir-descripcion', { body: { producto_id: productoId } })
      if (error || data?.error) {
        setMsg({ tipo: 'err', txt: 'IA: ' + (error?.message || data?.error) })
      } else if (data?.descripcion) {
        update('descripcion', data.descripcion)
        setMsg({ tipo: 'ok', txt: `Descripción generada (${data.chars} caracteres · ${data.duration_ms}ms)` })
      }
    } catch (e) { setMsg({ tipo: 'err', txt: e.message }) }
    finally { setGenerando(false) }
  }
  return (
    <>
      <h2>5 · Descripción larga</h2>
      <div className="wz-step-desc">
        La descripción final = <strong>cuerpo específico del producto</strong> + <strong>bloque fijo institucional</strong> (5 partes: Horario · Envíos · Medios de Pago · Precios · Sobre Limpack). El bloque fijo se mantiene en un solo lugar y la edge fn lo anexa sola al final; vos solo editás el cuerpo.
        <span style={{ color: '#888780', display: 'block', marginTop: 4 }}>El bloque fijo se edita en <Link to="/admin/canales/mercadolibre/descripcion-fija" style={{ color: '#00A8B5' }}>Bloque fijo descripción →</Link>.</span>
      </div>

      <div className="wz-form-row">
        <label className="wz-lbl">Cuerpo específico del producto ✨</label>
        <textarea
          className="wz-input wz-textarea"
          rows={8}
          value={pub.descripcion}
          onChange={e => update('descripcion', e.target.value)}
          placeholder="Qué resuelve, para qué tipo de cliente, en qué momento se usa. La IA te puede sugerir un primer borrador (próximamente)."
        />
        <div className={`wz-help ${len < 300 ? 'warn' : 'ok'}`}>
          {len} caracteres · objetivo 400-600 {len < 300 && '· muy corto para SEO'}
        </div>
        <button className="wz-btn-ghost" style={{ marginTop: 8 }} onClick={sugerirIA} disabled={generando}>
          {generando ? '✨ Generando…' : '✨ Sugerir con IA (Claude)'}
        </button>
      </div>

      <div className="wz-form-row">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <label className="wz-lbl">Bloque fijo institucional (read-only · se anexa solo al final)</label>
          <Link to="/admin/canales/mercadolibre/descripcion-fija" style={{ color: '#00A8B5', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>Editar bloque fijo →</Link>
        </div>
        {config.descripcion_bloques.length === 0 ? (
          <div className="wz-input wz-readonly" style={{ color: '#888780' }}>Sin bloques cargados todavía. Configuralos en Bloque fijo descripción.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {config.descripcion_bloques.map((b, i) => (
              <div key={i} className="wz-input wz-readonly" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{b}</div>
            ))}
          </div>
        )}
      </div>

      <div className="wz-info">
        <strong>Vista previa de la descripción final</strong> (lo que va a ver el comprador en ML):
        <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontFamily: 'inherit', fontSize: 12, color: '#1A3461', background: '#fff', padding: 10, borderRadius: 6, border: '1px solid #E6E2D8' }}>
{[pub.descripcion, ...config.descripcion_bloques].filter(Boolean).join('\n\n')}
        </pre>
      </div>
    </>
  )
}

function Step6({ producto, fotos, onSubir, onEliminar, onPortada, onImportarLimpack, onGenerarIA, onAbrirGrande, onArmarAuto, armandoAuto, onMover }) {
  // Contextos LIFESTYLE (cocina, lavadero, en_mano) sacados 13-jul: incluso Flux Kontext Max
  // reinterpreta las etiquetas cuando genera fondos ambientados con producto.
  // Se dejan solo los 3 que preservan bien: fondo_blanco (packshot MELI), close_up (crop sin IA),
  // vista cenital (Kontext Max top-down preserva razonable).
  const CONTEXTOS_DISPONIBLES = [
    { id: 'fondo_blanco',  label: 'Fondo blanco puro',  tip: 'Portada MELI · etiquetas preservadas' },
    { id: 'close_up',      label: 'Close-up etiqueta',  tip: 'Crop centrado de la foto real · gratis · etiqueta 100% intacta', seguro: true },
    { id: 'cenital',       label: 'Vista cenital',      tip: 'Top-down · etiquetas preservadas' },
  ]
  const [seleccionados, setSeleccionados] = useState(['fondo_blanco', 'close_up', 'cenital'])
  const [generando, setGenerando] = useState(false)
  const yaImportoLimpack = fotos.some(f => f.contexto === 'foto_limpack_uy')
  const tieneImagenLimpack = !!producto?.imagen_url

  function toggleContexto(id) {
    setSeleccionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])
  }

  async function handleGenerar() {
    setGenerando(true)
    await onGenerarIA(seleccionados)
    setGenerando(false)
  }

  return (
    <>
      <h2>6 · Fotos del producto</h2>
      <div className="wz-step-desc">Hasta 12 fotos. La primera (portada) impacta el CTR. ML recomienda mínimo 6 fotos · fondo blanco rankea mejor.</div>

      {/* ✨ Botón mágico: armar todo de una. Aparece mientras falte algo del set ideal. */}
      {(() => {
        const tienePortadaBlanco = fotos.some(f => f.contexto === 'fondo_blanco')
        const tieneCenital = fotos.some(f => f.contexto === 'cenital')
        const setCompleto = tienePortadaBlanco && tieneCenital
        if (!tieneImagenLimpack || setCompleto) return null
        const faltantes = []
        if (!yaImportoLimpack) faltantes.push('foto lifestyle')
        if (!tienePortadaBlanco) faltantes.push('portada fondo blanco')
        if (!tieneCenital) faltantes.push('vista cenital')
        return (
          <div style={{
            background: 'linear-gradient(135deg, rgba(0,168,181,0.08), rgba(26,52,97,0.06))',
            border: '1.5px solid rgba(0,168,181,0.35)',
            borderRadius: 12,
            padding: 18,
            marginBottom: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}>
            <div style={{ fontSize: 36 }}>✨</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#1A3461', fontSize: 14, marginBottom: 3 }}>Armar galería automática</div>
              <div style={{ fontSize: 12.5, color: '#5F5E5A', lineHeight: 1.4 }}>
                Falta: <strong>{faltantes.join(' + ')}</strong>. Las 2 placas Limpack se anexan solas al final. Costo restante: <strong>~USD ${(faltantes.length * 0.08).toFixed(2)}</strong>.
              </div>
            </div>
            <button
              className="wz-btn-primary"
              onClick={onArmarAuto}
              disabled={armandoAuto}
              style={{ minWidth: 180 }}
            >
              {armandoAuto ? 'Armando…' : '✨ Armar lo que falta'}
            </button>
          </div>
        )
      })()}

      {/* Sección: importar foto limpack.uy como base */}
      {tieneImagenLimpack && !yaImportoLimpack && (
        <div className="wz-card" style={{ background: '#E0F4F6', borderColor: '#00A8B5', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <img src={producto.imagen_url} alt="foto limpack.uy" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #E6E2D8' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#1A3461', fontSize: 13 }}>Reusar foto que ya tenés en limpack.uy</div>
              <div style={{ fontSize: 11.5, color: '#5F5E5A', marginTop: 2 }}>
                La imagen de tu tienda sirve como <strong>foto base</strong>. Click para importarla y después generar variantes en distintos contextos.
              </div>
            </div>
            <button className="wz-btn-primary" onClick={onImportarLimpack}>↓ Importar como base</button>
          </div>
        </div>
      )}

      {/* Sección: generador IA */}
      {fotos.length > 0 && (
        <div className="wz-card" style={{ background: '#EEEDFE', borderColor: '#7F77DD', marginBottom: 14 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#534AB7' }}>✨ Generar variantes con IA</h4>
          <div style={{ fontSize: 12, color: '#534AB7', marginBottom: 12 }}>
            Elegí en qué contextos querés ver el producto. Replicate Flux genera cada variante manteniendo el envase fiel y cambiando solo el entorno.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {CONTEXTOS_DISPONIBLES.map(c => (
              <span
                key={c.id}
                onClick={() => toggleContexto(c.id)}
                title={c.tip}
                style={{
                  padding: '5px 11px',
                  borderRadius: 999,
                  fontSize: 11.5,
                  cursor: 'pointer',
                  background: seleccionados.includes(c.id) ? '#7F77DD' : 'white',
                  color: seleccionados.includes(c.id) ? 'white' : '#534AB7',
                  border: c.seguro ? '1px solid #10B981' : '1px solid #7F77DD',
                  fontWeight: 600,
                  userSelect: 'none',
                }}
              >
                {seleccionados.includes(c.id) ? '✓ ' : ''}{c.label}{c.seguro ? ' 🛡' : ''}
              </span>
            ))}
          </div>

          {/* Warning específico para close-up cuando la base puede venir de Flux */}
          {seleccionados.includes('close_up') && yaImportoLimpack && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.35)',
              borderRadius: 8,
              fontSize: 11.5,
              color: '#854F0B',
              marginBottom: 12,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 14, lineHeight: 1.2 }}>⚠️</span>
              <div style={{ flex: 1, lineHeight: 1.45 }}>
                <strong>Close-up etiqueta:</strong> recorta tu foto base sin IA, pero si la base de limpack.uy fue procesada con Flux (caso de muchos productos), la etiqueta puede tener texto inventado y el close-up lo perpetúa.
                <div style={{ marginTop: 6 }}>
                  Para etiqueta 100% confiable, sacá foto real del envase con celular:
                  <label style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    background: '#1A3461',
                    color: '#fff',
                    padding: '5px 11px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginLeft: 8,
                  }}>
                    📷 Subir foto real
                    <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => onSubir(e.target.files[0], 'foto_original')} />
                  </label>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11.5, color: '#534AB7' }}>
              {seleccionados.length} variantes seleccionadas · costo estimado USD ${(
                seleccionados.reduce((sum, id) => {
                  if (id === 'close_up') return sum + 0     // crop canvas local: gratis
                  return sum + 0.08                          // Flux Kontext Max (v7+)
                }, 0)
              ).toFixed(3)}
            </span>
            <button onClick={handleGenerar} disabled={generando || seleccionados.length === 0} className="wz-btn-primary" style={{ background: '#7F77DD' }}>
              {generando ? '✨ Generando…' : `✨ Generar ${seleccionados.length}`}
            </button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 8, fontSize: 11, color: '#888780', letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 600 }}>
        Galería del producto ({fotos.length}/10) · usá ◀ ▶ para reordenar · la #1 es la portada
      </div>
      <div className="wz-fotos-grid">
        {fotos.map((f, i) => (
          <div key={f.id} style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: 6, left: 6, zIndex: 2, background: 'rgba(26,52,97,0.85)', color: '#fff', fontSize: 11, fontWeight: 700, width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
            {/* Botones reordenar */}
            <div style={{ position: 'absolute', top: 6, right: 6, zIndex: 2, display: 'flex', gap: 4 }}>
              {i > 0 && (
                <button
                  onClick={() => onMover && onMover(f, -1)}
                  title="Mover hacia atrás (más cerca de portada)"
                  style={{ background: 'rgba(26,52,97,0.85)', color: '#fff', border: 'none', width: 22, height: 22, borderRadius: '50%', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                >◀</button>
              )}
              {i < fotos.length - 1 && (
                <button
                  onClick={() => onMover && onMover(f, +1)}
                  title="Mover hacia adelante"
                  style={{ background: 'rgba(26,52,97,0.85)', color: '#fff', border: 'none', width: 22, height: 22, borderRadius: '50%', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                >▶</button>
              )}
            </div>
            <FotoSlot foto={f} onEliminar={() => onEliminar(f)} onPortada={() => onPortada(f)} onAbrirGrande={onAbrirGrande} />
          </div>
        ))}
        {fotos.length < 10 && (
          <label className="wz-foto-add">
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onSubir(e.target.files[0])} />
            <div>+ Subir manual</div>
            <div style={{ fontSize: 10.5, color: '#888780', marginTop: 4 }}>JPG, PNG, max 5 MB</div>
          </label>
        )}
      </div>
      {fotos.length === 0 && !tieneImagenLimpack && (
        <div className="wz-info">El producto no tiene <code>imagen_url</code> cargada en limpack.uy. Empezá subiendo al menos 1 foto manual abajo.</div>
      )}

      <FotosFijasCandado />
    </>
  )
}

// ───────────────── FOTOS FIJAS (candado al final) ─────────────────
function FotosFijasCandado() {
  const [placas, setPlacas] = useState([])
  const [urls, setUrls] = useState({})

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('ml_fotos_fijas')
        .select('id, slug, titulo, storage_bucket, storage_path, orden')
        .eq('activa', true)
        .order('orden')
      setPlacas(data || [])
      if (data) {
        const u = {}
        for (const p of data) {
          const { data: s } = await supabase.storage.from(p.storage_bucket).createSignedUrl(p.storage_path, 3600)
          if (s?.signedUrl) u[p.slug] = s.signedUrl
        }
        setUrls(u)
      }
    })()
  }, [])

  if (placas.length === 0) return null

  return (
    <div style={{ marginTop: 28, padding: 16, background: 'linear-gradient(135deg, rgba(26,52,97,0.04), rgba(0,168,181,0.04))', border: '1px solid rgba(26,52,97,0.12)', borderRadius: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 16 }}>🔒</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1A3461', letterSpacing: 0.2 }}>Fotos Limpack · se anexan a toda publicación</div>
          <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>
            Estas {placas.length} placas se agregan automáticamente al final de la galería ML. Para editarlas: <a href="/admin/canales/mercadolibre/fotos-fijas" style={{ color: '#00A8B5', textDecoration: 'none', fontWeight: 600 }}>↗ Fotos fijas</a>
          </div>
        </div>
      </div>
      <div className="wz-fotos-grid">
        {placas.map(p => (
          <div key={p.id} style={{ position: 'relative', aspectRatio: '1', background: '#F8F6F1', border: '2px solid rgba(26,52,97,0.12)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 6, left: 6, zIndex: 2, background: 'rgba(26,52,97,0.85)', color: '#fff', fontSize: 11, fontWeight: 700, width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(p.orden || 0) + 10}</div>
            <div style={{ position: 'absolute', top: 6, right: 6, zIndex: 2, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 10, padding: '2px 8px', borderRadius: 4 }}>🔒 Fija</div>
            {urls[p.slug] ? (
              <img src={urls[p.slug]} alt={p.titulo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#888', textAlign: 'center', padding: 8 }}>Sin renderizar.<br/>Generala desde Fotos fijas.</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function FotoSlot({ foto, onEliminar, onPortada, onAbrirGrande }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    supabase.storage.from(foto.storage_bucket).createSignedUrl(foto.storage_path, 3600).then(({ data }) => {
      if (data) setUrl(data.signedUrl)
    })
  }, [foto.storage_path])
  return (
    <div className={`wz-foto-slot ${foto.es_portada ? 'portada' : ''}`}>
      {foto.es_portada && <span className="wz-foto-badge">Portada</span>}
      {url ? (
        <img
          src={url}
          alt=""
          style={{ cursor: 'zoom-in' }}
          onClick={() => onAbrirGrande && onAbrirGrande({ ...foto, url })}
          title="Click para ver en grande"
        />
      ) : (
        <div style={{ color: '#888780', fontSize: 11, textAlign: 'center' }}>cargando…</div>
      )}
      <div className="wz-foto-acts">
        {!foto.es_portada && <button onClick={onPortada} title="Marcar como portada">★</button>}
        <button onClick={onEliminar} title="Eliminar" className="rm">×</button>
      </div>
    </div>
  )
}

function Lightbox({ foto, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(8,12,24,.92)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, cursor: 'zoom-out'
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,.1)',
          color: '#fff', border: '1px solid rgba(255,255,255,.3)', borderRadius: 8,
          padding: '6px 12px', cursor: 'pointer', fontSize: 14
        }}
      >✕ Cerrar</button>
      <img
        src={foto.url}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain',
          borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.6)',
          cursor: 'default'
        }}
      />
      {foto.es_portada && (
        <div style={{
          position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#C5A059', color: '#1A3461', padding: '6px 14px', borderRadius: 999,
          fontSize: 12, fontWeight: 700
        }}>★ Portada</div>
      )}
    </div>
  )
}

function Step7({ producto, pub, update, config, feeML, neto, dif }) {
  const tipoElegido = LISTING_TYPES.find(t => t.id === pub.listing_type)
  const markup = producto.precio_publico > 0 ? Math.round((pub.precio - producto.precio_publico) / producto.precio_publico * 100) : 0
  const disponible = Math.max(0, Number(producto.stock_actual || 0) - Number(producto.stock_reservado || 0))

  // Tarifa real de Mercado Envíos UY 2026 (precios públicos del panel "Conoce los costos por vender").
  // ML cobra TARIFA ÚNICA por peso (ya promedió destinos). Productos ≥ $1.000 tienen 50% off.
  function tarifaEnvio(pesoG, precio) {
    if (!pesoG) return null
    const kg = pesoG / 1000
    let base
    if      (kg <= 0.5) base = 235
    else if (kg <= 1)   base = 250
    else if (kg <= 2)   base = 265
    else if (kg <= 3)   base = 275
    else if (kg <= 5)   base = 290
    else if (kg <= 8)   base = 315
    else if (kg <= 10)  base = 365
    else if (kg <= 15)  base = 445
    else if (kg <= 20)  base = 490
    else if (kg <= 25)  base = 535
    else if (kg <= 30)  base = 585
    else if (kg <= 35)  base = 635
    else                base = 1055 // 35-40 kg (simplificado para >35kg)
    // Productos nuevos ≥ $1000 → 50% descuento
    if (precio >= 1000) return Math.round(base * 0.5)
    return base
  }

  const tarifaUnica = tarifaEnvio(pub.peso_g, pub.precio)
  const tieneDescuento = pub.precio >= 1000
  const netoFinal = pub.free_shipping && tarifaUnica ? neto - tarifaUnica : neto

  return (
    <>
      <h2>7 · Precio, dimensiones y stock</h2>
      <div className="wz-step-desc">Dimensiones del producto son obligatorias para que ML calcule la tarifa de envío. Si activás "envío gratis", vos absorbés esa tarifa.</div>

      <div className="wz-form-row">
        <label className="wz-lbl">Precio web (referencia)</label>
        <div className="wz-input wz-readonly">$ {Number(producto.precio_publico || 0).toLocaleString('es-UY')}</div>
      </div>
      <div className="wz-form-row">
        <label className="wz-lbl">Precio en ML (con IVA) — 100% editable</label>
        <input type="number" className="wz-input" value={pub.precio} onChange={e => update('precio', Number(e.target.value))} onBlur={e => update('precio', Math.ceil(Number(e.target.value) || 0))} min="1" step="1" />
        <div className="wz-help">
          Markup actual: <strong>{markup}%</strong> sobre el precio web. <br/>
          Podés escribir cualquier valor — el markup se recalcula solo. Tip: ML suele funcionar mejor con precio 15-25% arriba del sitio propio (anclaje: la gente encuentra Limpack en ML, va al sitio y ve más barato).
        </div>
      </div>
      <div className="wz-form-row">
        <label className="wz-lbl">Stock disponible para ML</label>
        <input type="number" className="wz-input" value={pub.available_quantity} onChange={e => update('available_quantity', Number(e.target.value))} min="1" />
        <div className={`wz-help ${Number(pub.available_quantity) > disponible ? 'warn' : ''}`}>
          Disponible real: <strong>{disponible}</strong> u. (stock − reservado).
          {Number(pub.available_quantity) > disponible && ' ⚠ Estás publicando más de lo que tenés — si se vende, quedás sin stock.'}
        </div>
      </div>

      <div style={{ borderTop: '1px solid #F1EFE8', paddingTop: 12, marginTop: 8 }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, color: '#1A3461' }}>Dimensiones del producto (con packaging)</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
          <div className="wz-form-row" style={{ margin: 0 }}>
            <label className="wz-lbl">Peso (g)</label>
            <input type="number" className="wz-input" defaultValue={pub.peso_g || ''} onChange={e => update('peso_g', e.target.value ? Number(e.target.value) : null)} placeholder="ej. 850" />
          </div>
          <div className="wz-form-row" style={{ margin: 0 }}>
            <label className="wz-lbl">Largo (cm)</label>
            <input type="number" className="wz-input" defaultValue={pub.largo_cm || ''} onChange={e => update('largo_cm', e.target.value ? Number(e.target.value) : null)} placeholder="ej. 25" />
          </div>
          <div className="wz-form-row" style={{ margin: 0 }}>
            <label className="wz-lbl">Ancho (cm)</label>
            <input type="number" className="wz-input" defaultValue={pub.ancho_cm || ''} onChange={e => update('ancho_cm', e.target.value ? Number(e.target.value) : null)} placeholder="ej. 8" />
          </div>
          <div className="wz-form-row" style={{ margin: 0 }}>
            <label className="wz-lbl">Alto (cm)</label>
            <input type="number" className="wz-input" defaultValue={pub.alto_cm || ''} onChange={e => update('alto_cm', e.target.value ? Number(e.target.value) : null)} placeholder="ej. 8" />
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: '#F8F6F1', borderRadius: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={pub.free_shipping} onChange={e => update('free_shipping', e.target.checked)} />
          Ofrecer envío gratis (vos absorbés el costo)
        </label>
        <div style={{ fontSize: 11.5, color: '#888780', marginTop: 4, marginLeft: 24 }}>
          ML te destaca con etiqueta verde "Envío gratis" y subís +30-40% en visibilidad. Pierde sentido si tu margen es chico.
        </div>
      </div>

      <div className="wz-card" style={{ marginTop: 14, background: '#FFFBEF', borderColor: '#E6D9B8' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, color: '#1A3461' }}>Cálculo de neto por destino</h4>
        {!feeML.conocido && (
          <div style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: 6, padding: '8px 10px', marginBottom: 8, fontSize: 12, color: '#334155', lineHeight: 1.5 }}>
            El neto depende del tipo de publicación, que se elige en el paso 8. Elegilo y este cálculo se actualiza solo.
          </div>
        )}
        <div className="wz-prod-row"><span>Precio ML</span><strong>$ {Number(pub.precio || 0).toLocaleString('es-UY')}</strong></div>
        {feeML.conocido && feeML.pct > 0 && (
          <div className="wz-prod-row"><span>Comisión {tipoElegido.label} ({feeML.pct}%)</span><strong>−$ {feeML.variable.toLocaleString('es-UY')}</strong></div>
        )}
        {feeML.conocido && feeML.pct === 0 && (
          <div className="wz-prod-row"><span>Comisión {tipoElegido.label}</span><strong>$ 0</strong></div>
        )}
        {feeML.fijo > 0 && <div className="wz-prod-row"><span>Costo fijo ML (por tramo de precio)</span><strong>−$ {feeML.fijo}</strong></div>}
        <div className="wz-prod-row" style={{ borderTop: '1px solid #E6D9B8', paddingTop: 6, marginTop: 6 }}>
          <span><strong>Subtotal antes de envío</strong></span>
          <strong>$ {neto.toLocaleString('es-UY')}</strong>
        </div>
        {pub.peso_g ? (
          <>
            <div className="wz-prod-row" style={{ paddingTop: 8, borderTop: '1px dashed #E6D9B8', marginTop: 4 }}>
              <span>📦 Tarifa de envío gratis ML ({pub.peso_g}g)</span>
              <strong>
                {pub.free_shipping ? `−$ ${tarifaUnica}` : `$ ${tarifaUnica} (no aplicada · paga comprador)`}
                {tieneDescuento && <span style={{ color: '#1D9E75', fontSize: 10, marginLeft: 6 }}>−50% off</span>}
              </strong>
            </div>
            <div className="wz-prod-row" style={{ paddingTop: 6 }}>
              <span><strong>Neto final para Limpack</strong></span>
              <strong style={{ color: netoFinal >= producto.precio_publico ? '#1D9E75' : '#C53030', fontSize: 16 }}>
                $ {netoFinal.toLocaleString('es-UY')}
              </strong>
            </div>
            <div className="wz-prod-row">
              <span>Diferencia vs precio web</span>
              <strong style={{ color: (netoFinal - producto.precio_publico) >= 0 ? '#1D9E75' : '#C53030' }}>
                {(netoFinal - producto.precio_publico) >= 0 ? '+' : ''}$ {(netoFinal - producto.precio_publico).toLocaleString('es-UY')}
              </strong>
            </div>
            {tieneDescuento && (
              <div style={{ fontSize: 11, color: '#1D9E75', marginTop: 8, padding: '6px 10px', background: '#E1F5EE', borderRadius: 6, fontWeight: 600 }}>
                ✓ Producto ≥ $1.000 → ML te da 50% off en envío gratis
              </div>
            )}
            {!tieneDescuento && pub.free_shipping && (
              <div style={{ fontSize: 11, color: '#BA7517', marginTop: 8, padding: '6px 10px', background: '#FAEEDA', borderRadius: 6 }}>
                ⚠ Producto &lt; $1.000 → envío gratis a costo completo. Considerá subir el precio para entrar al descuento del 50%.
              </div>
            )}
            {!pub.free_shipping && (
              <div style={{ fontSize: 11, color: '#888780', marginTop: 8, fontStyle: 'italic' }}>
                Sin envío gratis: comprador paga la tarifa. Tu neto queda libre del costo, pero perdés visibilidad (sin etiqueta verde "Envío gratis").
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: '#BA7517', marginTop: 8, padding: '8px 10px', background: '#FAEEDA', borderRadius: 6 }}>
            ⚠ Cargá el peso arriba para calcular la tarifa y tu neto real.
          </div>
        )}
      </div>

      {/* Alerta: si el neto es menor que el precio web, está perdiendo plata vs limpack.uy */}
      {(() => {
        const precioWeb = Number(producto?.precio_publico || 0)
        const precioML = Number(pub.precio || 0)
        // Sin tipo elegido no hay comisión real que comparar: el neto todavía no significa nada.
        if (!feeML.conocido || precioWeb <= 0 || precioML <= 0 || neto <= 0) return null
        const diferencia = neto - precioWeb
        if (diferencia >= 0) return null // ganás o empatás, no hay alerta
        // Markup que dejaría el neto >= precio web + 10%, cubriendo la comisión real de ML.
        // (Antes usaba pub.markup_pct, que no existe en el estado → siempre caía a 28 y calculaba mal.)
        const objetivoNeto = precioWeb * 1.10 + feeML.total
        const markupSugerido = precioWeb > 0 ? Math.max(markup + 5, Math.ceil((objetivoNeto / precioWeb - 1) * 100)) : markup + 15
        return (
          <div style={{
            marginTop: 14,
            padding: '14px 16px',
            background: 'linear-gradient(135deg, rgba(239,68,68,0.06), rgba(245,158,11,0.04))',
            border: '1.5px solid rgba(239,68,68,0.35)',
            borderRadius: 10,
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <div style={{ fontSize: 22, lineHeight: 1 }}>⚠️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#991B1B', fontSize: 13, marginBottom: 4 }}>
                Perdés ${Math.abs(diferencia).toLocaleString('es-UY')} por unidad vs vender en limpack.uy
              </div>
              <div style={{ fontSize: 12, color: '#7F1D1D', lineHeight: 1.5 }}>
                Precio web <strong>${precioWeb.toLocaleString('es-UY')}</strong> · Neto ML <strong>${neto.toLocaleString('es-UY')}</strong>.
                Con markup {markup}% no llegás a cubrir el costo de ML en {tipoElegido.label} (${feeML.total.toLocaleString('es-UY')} por venta).
              </div>
              <div style={{ fontSize: 12, color: '#7F1D1D', marginTop: 6 }}>
                <strong>Opciones:</strong>
                <ul style={{ margin: '4px 0 0 18px' }}>
                  <li>Subí el markup a <strong>{markupSugerido}%+</strong> para anclar a precio web + 10%</li>
                  <li>Publicalo como <strong>pack de 4-6 unidades</strong> (precio total ≥ $1.000 = sin costo fijo + envío 50% off)</li>
                  <li>Sacalo de ML (no es rentable suelto a este precio)</li>
                </ul>
              </div>
            </div>
          </div>
        )
      })()}
    </>
  )
}

function Step8({ pub, update }) {
  return (
    <>
      <h2>8 · Tipo de publicación</h2>
      <div className="wz-step-desc">
        Define <strong>cuánto pagás de comisión</strong>, si <strong>ofrecés cuotas sin interés</strong>, tu <strong>exposición en búsqueda</strong> y <strong>cuánto dura la publicación</strong>.
      </div>
      {!pub.listing_type && (
        <div style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12.5, color: '#334155', lineHeight: 1.6 }}>
          Elegí el tipo de publicación para este producto. No hay opción por defecto: se define uno por uno.
        </div>
      )}
      {LISTING_TYPES.map(t => {
        const seleccionado = pub.listing_type === t.id
        return (
          <label key={t.id} className={`wz-option ${seleccionado ? 'selected' : ''}`} style={{ padding: 14, marginBottom: 10 }}>
            <input type="radio" name="listing" value={t.id} checked={seleccionado} onChange={() => update('listing_type', t.id)} />
            <div style={{ flex: 1 }}>
              <div className="wz-option-title" style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{t.label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                <span style={{ color: '#6B7280' }}>Comisión</span><strong>{t.comision}</strong>
                <span style={{ color: '#6B7280' }}>Exposición</span><span>{t.exposicion}</span>
                <span style={{ color: '#6B7280' }}>Duración</span><span>{t.duracion}</span>
                <span style={{ color: '#6B7280' }}>Cuotas</span><span>{t.cuotas}</span>
              </div>
              {t.nota && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: '#6B7280', lineHeight: 1.5 }}>{t.nota}</div>
              )}
            </div>
          </label>
        )
      })}
    </>
  )
}

function Step9({ pub, update }) {
  return (
    <>
      <h2>9 · Modalidad de envío</h2>
      <div className="wz-step-desc">Mercado Envíos cubre todo Uruguay. Según cómo tengas configurada la cuenta, despachás por Flex (repartís vos, mismo día en Montevideo) o dejando el paquete en sucursal DAC/Correo. El comprador no ve la diferencia.</div>
      {SHIPPING_MODES.map(m => (
        <label key={m.id} className={`wz-option ${pub.shipping_mode === m.id ? 'selected' : ''}`}>
          <input type="radio" name="shipping" value={m.id} checked={pub.shipping_mode === m.id} onChange={() => update('shipping_mode', m.id)} />
          <div>
            <div className="wz-option-title">{m.label}</div>
            <div className="wz-option-meta">{m.meta}</div>
          </div>
        </label>
      ))}
      {pub.shipping_mode === 'me2' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
          <input type="checkbox" checked={pub.free_shipping} onChange={e => update('free_shipping', e.target.checked)} />
          Envío gratis (vos absorbés el costo, ML lo destaca y rankea mejor)
        </label>
      )}
    </>
  )
}

function Step10({ producto, pub, fotos, neto, guardarDraft, saving, tieneConexion, onPreview, preview }) {
  return (
    <>
      <h2>10 · Preview y publicar</h2>
      <div className="wz-step-desc">Revisá todo antes de mandar. Si ML está conectado, sale vivo. Si no, queda como draft listo para publicar después.</div>
      <div className="wz-card" style={{ background: '#FFFBEF', borderColor: '#E6D9B8' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 13, color: '#1A3461' }}>Resumen</h4>
        <div className="wz-prod-row"><span>Producto</span><strong>{producto.nombre}</strong></div>
        <div className="wz-prod-row"><span>Categoría ML</span><strong>{pub.categoria_id}</strong></div>
        <div className="wz-prod-row"><span>Título</span><strong>{pub.titulo}</strong></div>
        <div className="wz-prod-row"><span>Marca</span><strong>{pub.attributes.BRAND}</strong></div>
        <div className="wz-prod-row"><span>Precio ML</span><strong>$ {Number(pub.precio).toLocaleString('es-UY')}</strong></div>
        <div className="wz-prod-row"><span>Neto Limpack</span><strong style={{ color: neto >= producto.precio_publico ? '#1D9E75' : '#C53030' }}>$ {neto.toLocaleString('es-UY')}</strong></div>
        <div className="wz-prod-row"><span>Listing type</span><strong>{
          LISTING_TYPES.find(t => t.id === pub.listing_type)?.label
            ? `${LISTING_TYPES.find(t => t.id === pub.listing_type).label} (${pub.listing_type})`
            : <span style={{ color: '#B91C1C' }}>Sin elegir — paso 8</span>
        }</strong></div>
        <div className="wz-prod-row"><span>Envío</span><strong>{pub.shipping_mode}{pub.free_shipping && ' · gratis'}</strong></div>
        <div className="wz-prod-row"><span>Stock</span><strong>{pub.available_quantity}</strong></div>
        <div className="wz-prod-row"><span>Fotos</span><strong>{fotos.length}/12 · {fotos.length === 0 && <span style={{ color: '#C53030' }}>FALTA</span>}</strong></div>
      </div>
      {!tieneConexion && (
        <div className="wz-info">
          <strong>ML sin conectar todavía.</strong> Al guardar, queda registrado como borrador en <code>ml_publicaciones</code> con status <code>draft</code>. Cuando autorices la app en `/admin/canales/mercadolibre/configuracion`, podés publicar todos los drafts pendientes con 1 click.
        </div>
      )}

      {tieneConexion && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className="wz-btn-ghost" onClick={onPreview} disabled={saving}>
            {saving ? '…' : '🔍 Previsualizar payload (dry-run)'}
          </button>
          <span style={{ fontSize: 11, color: '#888780' }}>Muestra el JSON exacto que se manda a ML, sin publicar nada.</span>
        </div>
      )}

      {preview && (
        <div className="wz-card" style={{ marginTop: 12 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#1A3461' }}>Preview del payload MELI (dry-run)</h4>
          <div style={{ fontSize: 11.5, color: '#5F5E5A', marginBottom: 8 }}>
            {preview.fotos_a_subir} foto(s) se subirían · {preview.fotos_con_picture_id} ya con <code>picture_id</code>
          </div>
          {preview.dims_faltantes?.length > 0 && (
            <div style={{ padding: '8px 12px', background: '#FDEEEE', border: '1px solid #F0B4B4', borderRadius: 8, marginBottom: 8, fontSize: 11.5, color: '#791F1F', fontWeight: 600 }}>
              ⚠ Faltan dimensiones: {preview.dims_faltantes.join(', ')}. Sin ellas ML aplica el default (9799 g / 119 cm) y te infla el flete. Cargalas en el paso 7.
            </div>
          )}
          {preview.attrs_descartados?.length > 0 && (
            <div style={{ padding: '8px 12px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, marginBottom: 8, fontSize: 11.5, color: '#78350F', fontWeight: 600 }}>
              ⚠ ML descarta estos atributos en esta categoría (no van a subir): {preview.attrs_descartados.join(', ')}. Revisalos en el paso 3.
            </div>
          )}
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, background: '#0C162C', color: '#D9E4F0', padding: 12, borderRadius: 8, maxHeight: 360, overflow: 'auto', margin: 0 }}>
{JSON.stringify(preview.item_payload, null, 2)}
          </pre>
          <h4 style={{ margin: '12px 0 6px', fontSize: 12, color: '#1A3461' }}>Descripción final (primeros 500 chars)</h4>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, background: '#F8F6F1', color: '#1A3461', padding: 10, borderRadius: 6, margin: 0, maxHeight: 200, overflow: 'auto' }}>
{preview.descripcion_preview}
          </pre>
        </div>
      )}
    </>
  )
}

function Estilos() {
  return (
    <style>{`
      .wz-wrap { max-width: 1300px; margin: 0 auto; padding: 0 4px 60px; }
      .wz-head { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 18px; margin-bottom: 12px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .wz-eyebrow { font-size: 11px; color: #888780; }
      .wz-eyebrow a { color: #00A8B5; text-decoration: none; }
      .wz-title { margin: 4px 0; font-size: 22px; font-weight: 600; color: #1A3461; }
      .wz-sub { font-size: 12px; color: #5F5E5A; }
      .wz-pill { padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; }
      .wz-pill.warn { background: #FAEEDA; color: #633806; }

      .wz-msg { padding: 9px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 12.5px; cursor: pointer; }
      .wz-msg.ok { background: #E1F5EE; color: #085041; border: 1px solid #1D9E75; }
      .wz-msg.err { background: #FDEEEE; color: #791F1F; border: 1px solid #C53030; }

      .wz-layout { display: grid; grid-template-columns: 260px 1fr; gap: 12px; }
      @media (max-width: 980px) { .wz-layout { grid-template-columns: 1fr; } }

      .wz-steps { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 10px; position: sticky; top: 12px; align-self: start; max-height: calc(100vh - 24px); overflow-y: auto; }
      .wz-steps h4 { margin: 0 0 10px; padding: 0 8px; font-size: 11px; color: #888780; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700; }
      .wz-step { display: flex; gap: 10px; padding: 7px 8px; border-radius: 6px; font-size: 12.5px; cursor: pointer; }
      .wz-step:hover { background: #F8F6F1; }
      .wz-step.active { background: #E0F4F6; color: #00A8B5; font-weight: 600; }
      .wz-step.done { color: #5F5E5A; }
      .wz-step .num { width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid currentColor; font-size: 10.5px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
      .wz-step.done .num { background: #1D9E75; border-color: #1D9E75; color: white; }
      .wz-step.active .num { background: #00A8B5; border-color: #00A8B5; color: white; }
      .wz-step-lbl { flex: 1; line-height: 1.3; }
      .wz-step-sub { font-size: 10.5px; color: #888780; margin-top: 1px; font-weight: 400; }

      .wz-body { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 22px 26px; }
      .wz-body h2 { margin: 0 0 6px; font-size: 17px; color: #1A3461; font-weight: 600; }
      .wz-step-desc { font-size: 12.5px; color: #5F5E5A; margin-bottom: 16px; line-height: 1.5; }

      .wz-form-row { margin-bottom: 14px; }
      .wz-lbl { display: block; font-size: 11px; letter-spacing: 0.5px; color: #888780; text-transform: uppercase; font-weight: 600; margin-bottom: 5px; }
      .wz-input { width: 100%; padding: 9px 11px; border: 1px solid #E6E2D8; border-radius: 7px; font-size: 13px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box; }
      .wz-input:focus { border-color: #00A8B5; outline: 0; box-shadow: 0 0 0 3px #E0F4F6; }
      .wz-readonly { background: #F8F6F1; color: #5F5E5A; font-size: 12px; padding: 10px 12px; min-height: 20px; }
      .wz-textarea { resize: vertical; min-height: 200px; line-height: 1.5; font-family: inherit; }
      .wz-help { font-size: 11px; color: #888780; margin-top: 4px; }
      .wz-help.ok { color: #1D9E75; }
      .wz-help.warn { color: #BA7517; }

      .wz-option { display: flex; gap: 12px; padding: 12px 14px; border: 1px solid #E6E2D8; border-radius: 8px; margin-bottom: 8px; cursor: pointer; background: #fff; align-items: center; }
      .wz-option:hover { border-color: #00A8B5; }
      .wz-option.selected { border-color: #00A8B5; background: #E0F4F6; }
      .wz-option input { margin: 0; }
      .wz-option-title { font-weight: 600; font-size: 13px; color: #1A3461; }
      .wz-option-meta { font-size: 11px; color: #888780; margin-top: 2px; }

      .wz-card { background: #fff; border: 1px solid #E6E2D8; border-radius: 8px; padding: 12px 14px; }
      .wz-prod-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 12.5px; align-items: center; }
      .wz-prod-row span { color: #888780; }
      .wz-prod-row strong { color: #1A3461; }

      .wz-fotos-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 10px; }
      .wz-foto-slot { aspect-ratio: 1; background: #F8F6F1; border: 2px solid #E6E2D8; border-radius: 10px; position: relative; overflow: hidden; }
      .wz-foto-slot.portada { border-color: #00A8B5; border-width: 3px; }
      .wz-foto-slot img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .wz-foto-badge { position: absolute; top: 6px; left: 6px; background: #00A8B5; color: white; font-size: 9.5px; padding: 2px 7px; border-radius: 99px; font-weight: 700; letter-spacing: 0.3px; }
      .wz-foto-acts { position: absolute; bottom: 6px; right: 6px; display: flex; gap: 3px; }
      .wz-foto-acts button { background: rgba(255,255,255,0.95); border: 0; padding: 4px 8px; border-radius: 4px; font-size: 13px; cursor: pointer; }
      .wz-foto-acts button.rm { color: #C53030; font-weight: 700; }
      .wz-foto-add { aspect-ratio: 1; background: #F8F6F1; border: 2px dashed #B5B0A0; border-radius: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; font-size: 13px; color: #1A3461; font-weight: 500; }
      .wz-foto-add:hover { border-color: #00A8B5; color: #00A8B5; }

      .wz-info { background: #FFFBEF; border: 1px solid #E6D9B8; border-radius: 8px; padding: 12px 14px; font-size: 12px; color: #1A3461; margin-top: 14px; line-height: 1.5; }
      .wz-info code { font-family: 'SF Mono', monospace; background: white; padding: 1px 5px; border-radius: 3px; color: #00A8B5; font-size: 11px; }

      .wz-foot { display: flex; gap: 8px; align-items: center; margin-top: 22px; padding-top: 16px; border-top: 1px solid #F1EFE8; }
      .wz-step-counter { font-size: 11.5px; color: #888780; }
      .wz-btn-primary { background: #00A8B5; color: #fff; border: 0; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
      .wz-btn-primary:disabled { background: #94A3B8; cursor: not-allowed; }
      .wz-btn-ghost { background: #fff; color: #1A3461; border: 1px solid #E6E2D8; padding: 8px 14px; border-radius: 8px; font-size: 12.5px; cursor: pointer; font-family: inherit; }
      .wz-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }
    `}</style>
  )
}
